/**
 * Copyright 2013 Google, Inc.
 * Copyright 2015 Daishinsha Inc.
 * Copyright 2019 Vivliostyle Foundation
 *
 * Vivliostyle.js is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Vivliostyle.js is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Vivliostyle.js.  If not, see <http://www.gnu.org/licenses/>.
 *
 * @fileoverview Vgen - View tree generator.
 */
import * as Asserts from "./asserts";
import * as Base from "./base";
import * as Break from "./break";
import * as Css from "./css";
import * as CssCascade from "./css-cascade";
import * as CssPage from "./css-page";
import * as CssParser from "./css-parser";
import * as CssTokenizer from "./css-tokenizer";
import * as CssProp from "./css-prop";
import * as CssStyler from "./css-styler";
import * as Diff from "./diff";
import * as Display from "./display";
import * as Exprs from "./exprs";
import * as Font from "./font";
import { fontMetricProperties, type FontProperties } from "./font-metrics";
import * as LayoutHelper from "./layout-helper";
import * as RubyEmphasis from "./ruby-emphasis";
import * as Logging from "./logging";
import * as Matchers from "./matchers";
import * as Net from "./net";
import * as PageFloats from "./page-floats";
import * as Plugin from "./plugin";
import * as PseudoElement from "./pseudo-element";
import * as RepetitiveElement from "./repetitive-element";
import * as Scripts from "./scripts";
import * as SemanticFootnote from "./semantic-footnote";
import * as Task from "./task";
import * as TaskUtil from "./task-util";
import * as Urls from "./urls";
import * as Vtree from "./vtree";
import * as Layout from "./layout";
import { XmlDoc } from "./types";

const namespacePrefixMap: { [key: string]: string } = {};
const xhtmlElementsRenderedAsDiv = new Set([
  "base",
  "body",
  "head",
  "html",
  "link",
  "meta",
  "script",
  "style",
  "title",
]);

/**
 * Whether resolving the font size needs the metrics of the font (and thus its
 * properties, which are read from the computed style).
 */
function usesFontMetrics(fontSize: Css.Numeric): boolean {
  return /^(ex|cap|ch|ic)$/.test(fontSize.unit);
}

const currentFontRelativeUnitPattern = /^(em|ex|cap|ch|ic)$/i;

class CurrentFontRelativeLengthVisitor extends Css.Visitor {
  found = false;

  override visitNumeric(numeric: Css.Numeric): Css.Val | null {
    this.found ||= currentFontRelativeUnitPattern.test(numeric.unit);
    return null;
  }
}

function hasCurrentFontRelativeLength(value: Css.Val): boolean {
  // This is checked for every property of every element, and most values are
  // a single length or keyword. Only lists and functions contain values that
  // Css.Visitor visits.
  if (value instanceof Css.Numeric) {
    return currentFontRelativeUnitPattern.test(value.unit);
  }
  if (!(
    value instanceof Css.SpaceList ||
    value instanceof Css.CommaList ||
    value instanceof Css.Func
  )) {
    return false;
  }
  const visitor = new CurrentFontRelativeLengthVisitor();
  value.visit(visitor);
  return visitor.found;
}

/**
 * Scales the lengths in units of the current font (em, ex, cap, ch, ic) that
 * the browser resolves, to undo the font-size adjustment by layoutUnitAdj.
 * Without this, a box sized with such a unit shrinks with the adjusted font
 * and loses the room the adjustment makes for the text, e.g. 20 characters in
 * a `20ic` box wrap. (Issue #1590)
 *
 * `em` resolves against the adjusted font size itself, while the other units
 * resolve against the font metrics, which the browser may take from a smaller
 * font size, so each unit has its own factor.
 */
class FontSizeAdjustmentCompensator extends Css.FilterVisitor {
  scaledNegative = false;

  constructor(private readonly getFactor: (unit: string) => number) {
    super();
  }

  override visitNumeric(numeric: Css.Numeric): Css.Val {
    const unit = numeric.unit.toLowerCase();
    const factor = /^(em|ex|cap|ch|ic)$/.test(unit) ? this.getFactor(unit) : 1;
    if (factor === 1) {
      return numeric;
    }
    this.scaledNegative ||= numeric.num < 0;
    return new Css.AnyToken(`calc(${numeric.toString()} * ${factor})`);
  }
}

/**
 * Whether a font-size declaration of a view element carries the font-size
 * adjustment by layoutUnitAdj: true for a generated
 * `calc(<length> - var(--viv-layoutUnitAdj))`, null for a value that takes
 * the adjustment of the parent font size along (a percentage, a relative unit
 * or keyword, or a CSS-wide value that means inheritance), and false for any
 * other value, e.g. `clamp(16px, 16px, 16px)`, which is not adjusted.
 * (Issue #1590)
 */
function fontSizeCarriesAdjustment(declaration: string): boolean | null {
  if (/-\s*var\(--viv-layoutUnitAdj\)\s*\)\s*$/.test(declaration)) {
    return true;
  }
  if (
    /^(inherit|unset)$/i.test(declaration.trim()) ||
    /%|\d(em|ex|cap|ch|ic|lh)\b|\b(larger|smaller)\b/i.test(declaration)
  ) {
    return null;
  }
  return false;
}

/**
 * The view elements whose font-size declaration looks as if it took the
 * adjustment of the parent font size along (see fontSizeCarriesAdjustment()),
 * but resolves to a length that the adjustment does not change, e.g.
 * `clamp(16px, 1em, 24px)` with a 16px parent, whose 16px minimum wins.
 * (Issue #1590)
 */
const fontSizesWithoutParentAdjustment = new WeakSet<Element>();

function getWebKitMarkerListStyleType(markerContent: Css.Val): Css.Str | null {
  if (markerContent instanceof Css.Str) {
    return markerContent;
  }
  if (markerContent instanceof Css.SpaceList) {
    const parts: string[] = [];
    for (const item of markerContent.values) {
      if (item instanceof Css.Str) {
        parts.push(item.str);
      } else {
        return null;
      }
    }
    if (parts.length > 0) {
      return new Css.Str(parts.join(""));
    }
  }
  return null;
}

function getWebKitMarkerListStyleImage(markerContent: Css.Val): Css.URL | null {
  if (markerContent instanceof Css.URL) {
    return markerContent;
  }
  if (
    markerContent instanceof Css.SpaceList &&
    markerContent.values[0] instanceof Css.URL &&
    markerContent.values
      .slice(1)
      .every((item) => item instanceof Css.Str && item.str.trim() === "")
  ) {
    return markerContent.values[0];
  }
  return null;
}

function applyWebKitMarkerFallback(computedStyle: {
  [key: string]: Css.Val;
}): boolean {
  if (Base.browserType !== "webkit") {
    return false;
  }

  const markerContent = computedStyle["--viv-marker-content"];
  if (!markerContent) {
    return false;
  }

  const listStyleImage = getWebKitMarkerListStyleImage(markerContent);
  if (listStyleImage) {
    computedStyle["list-style-type"] = Css.ident.none;
    computedStyle["list-style-image"] = listStyleImage;
    delete computedStyle["--viv-marker-content"];
    return true;
  }

  const listStyleType = getWebKitMarkerListStyleType(markerContent);
  if (listStyleType) {
    computedStyle["list-style-type"] = listStyleType;
    computedStyle["list-style-image"] = Css.ident.none;
    return true;
  }

  return false;
}

export type CustomRenderer = (
  p1: Element,
  p2: Element,
  p3: { [key: string]: Css.Val },
) => Task.Result<Element | null>;

export interface CustomRendererFactory {
  makeCustomRenderer(xmldoc: XmlDoc.XMLDocHolder): CustomRenderer;
}

/**
 * Creates an epubReadingSystem object in the iframe.contentWindow.navigator
 * when load event fires.
 */
export function initIFrame(iframe: HTMLIFrameElement): void {
  iframe.addEventListener(
    "load",
    () => {
      // the load event fires only when the iframe has a browsing context
      iframe.contentWindow!.navigator["epubReadingSystem"] = {
        name: "adapt",
        version: "0.1",
        layoutStyle: "paginated",
        hasFeature: function (name, version) {
          switch (name) {
            case "mouse-events":
              return true;
          }
          return false;
        },
      };
    },
    false,
  );
}

export interface StylerProducer {
  getStylerForDoc(xmldoc: XmlDoc.XMLDocHolder): CssStyler.AbstractStyler;
}

export class ViewFactory
  extends Base.SimpleEventTarget
  implements Vtree.LayoutContext
{
  private static SVG_URL_ATTRIBUTES: string[] = [
    "color-profile",
    "clip-path",
    "cursor",
    "filter",
    "marker",
    "marker-start",
    "marker-end",
    "marker-mid",
    "fill",
    "stroke",
    "mask",
  ];
  private static FOOTNOTE_CALL_OWNER_ATTR = "data-viv-footnote-call-owner";
  document: Document;
  exprContentListener: Vtree.ExprContentListener;
  private computedStyleParentFontSizeOverride: number | null = null;
  private computedStyleParentLineHeightOverride: number | null = null;

  // provided by layout
  nodeContext: Vtree.NodeContext | null = null;
  viewRoot: Element | null = null;
  isFootnote: boolean = false;
  sourceNode: Node | null = null;
  offsetInNode: number = 0;

  // computed
  // TODO: only set it on NodeContext
  viewNode: Element | Text | null = null;

  constructor(
    public readonly flowName: string,
    public readonly context: Exprs.Context,
    public readonly viewport: Viewport,
    public readonly styler: CssStyler.Styler,
    public readonly regionIds: string[] | null,
    public readonly xmldoc: XmlDoc.XMLDocHolder,
    public readonly docFaces: Font.DocumentFaces,
    public readonly footnoteStyle: CssCascade.ElementStyle,
    public readonly stylerProducer: StylerProducer,
    public readonly page: Vtree.Page,
    public readonly customRenderer: CustomRenderer,
    public readonly fallbackMap: { [key: string]: string },
    public readonly documentURLTransformer: Base.DocumentURLTransformer,
    public readonly pageProps?: { [key: string]: CssCascade.ElementStyle },
    public readonly cascadedPageStyle?: CssCascade.ElementStyle | null,
    private readonly semanticFootnoteFirstRefOffsets: Map<
      string,
      number
    > = new Map(),
    private readonly semanticFootnoteFirstRefOffsetsInitialized: {
      value: boolean;
    } = { value: false },
  ) {
    super();
    this.document = viewport.document;
    this.exprContentListener = styler.counterListener.getExprContentListener();
  }

  /** @override */
  clone(): Vtree.LayoutContext {
    return new ViewFactory(
      this.flowName,
      this.context,
      this.viewport,
      this.styler,
      this.regionIds,
      this.xmldoc,
      this.docFaces,
      this.footnoteStyle,
      this.stylerProducer,
      this.page,
      this.customRenderer,
      this.fallbackMap,
      this.documentURLTransformer,
      this.pageProps,
      this.cascadedPageStyle,
      this.semanticFootnoteFirstRefOffsets,
      this.semanticFootnoteFirstRefOffsetsInitialized,
    );
  }

  private getExplicitPageType(
    style: CssCascade.ElementStyle | null | undefined,
  ): string | null {
    const pageValue = style?.["page"] as CssCascade.CascadeValue;
    if (!pageValue || Css.isDefaultingValue(pageValue.value)) {
      return null;
    }
    const pageType = pageValue.evaluate(this.context, "page").toString();
    return !pageType || pageType.toLowerCase() === "auto" ? null : pageType;
  }

  private isNormalFlowElement(element: Element): boolean {
    const style = this.styler.getStyle(element, false);
    const displayValue = (
      style?.["display"] as CssCascade.CascadeValue
    )?.evaluate(this.styler.context, "display");
    const display =
      displayValue && !Css.isDefaultingValue(displayValue)
        ? (displayValue as Css.Ident)
        : Css.ident.inline;
    if (display === Css.ident.none) {
      return false;
    }
    const positionValue = (
      style?.["position"] as CssCascade.CascadeValue
    )?.evaluate(this.styler.context, "position");
    const position =
      positionValue && !Css.isDefaultingValue(positionValue)
        ? positionValue
        : Css.ident.static;
    if (
      Display.isRunning(position) ||
      Display.isAbsolutelyPositioned(position)
    ) {
      return false;
    }
    const floatValue = (style?.["float"] as CssCascade.CascadeValue)?.evaluate(
      this.styler.context,
      "float",
    );
    const float =
      floatValue && !Css.isDefaultingValue(floatValue)
        ? floatValue
        : Css.ident.none;
    return float === Css.ident.none;
  }

  private getFirstInFlowChildElement(root: Element): Element | null {
    let child = root.firstChild;
    while (child) {
      if (child.nodeType === Node.TEXT_NODE) {
        if (!Vtree.canIgnore(child)) {
          return null;
        }
        child = child.nextSibling;
        continue;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        child = child.nextSibling;
        continue;
      }
      const element = child as Element;
      if (this.isNormalFlowElement(element)) {
        const style = this.styler.getStyle(element, false);
        const displayValue = (
          style?.["display"] as CssCascade.CascadeValue
        )?.evaluate(this.styler.context, "display");
        const display =
          displayValue && !Css.isDefaultingValue(displayValue)
            ? (displayValue as Css.Ident)
            : Css.ident.inline;
        if (Display.isInlineLevel(display)) {
          return null;
        }
        return element;
      }
      child = child.nextSibling;
    }
    return null;
  }

  private getEffectiveStartPageType(
    element: Element,
    pageType: string | null,
  ): string | null {
    let effectivePageType = pageType;
    let currentElement: Element | null = element;
    while (currentElement) {
      const child = this.getFirstInFlowChildElement(currentElement);
      if (!child) {
        return effectivePageType;
      }
      const childPageType = this.getExplicitPageType(
        this.styler.getStyle(child, false),
      );
      if (childPageType) {
        effectivePageType = childPageType;
      }
      currentElement = child;
    }
    return effectivePageType;
  }

  private syncTextNodePageTypeBoundary(nodeContext: Vtree.NodeContext): void {
    if (
      nodeContext.sourceNode.nodeType !== Node.TEXT_NODE ||
      Vtree.canIgnore(nodeContext.sourceNode) ||
      nodeContext.fragmentIndex !== 1
    ) {
      return;
    }
    const pageType = nodeContext.pageType;
    if (this.styler.cascade.currentPageType === pageType) {
      return;
    }
    if (!Break.isSpreadBreakValue(nodeContext.breakBefore)) {
      nodeContext.breakBefore = Break.resolveEffectiveBreakValue(
        nodeContext.breakBefore,
        "page",
      );
    }
    if (pageType !== this.styler.cascade.previousPageType) {
      this.styler.cascade.previousPageType =
        this.styler.cascade.currentPageType;
    }
    this.styler.cascade.currentPageType = pageType;
  }

  private syncSemanticFootnoteCounterToTarget(element: Element): void {
    if (
      element.localName !== "a" ||
      !SemanticFootnote.isSemanticFootnoteNoterefElement(element) ||
      !SemanticFootnote.shouldGenerateSemanticFootnote(
        element,
        this.xmldoc.url,
        (noteref) => this.xmldoc.getElementOffset(noteref),
        this.semanticFootnoteFirstRefOffsets,
        this.semanticFootnoteFirstRefOffsetsInitialized,
      )
    ) {
      return;
    }
    const footnoteCounter = element.getAttribute(
      CssCascade.FOOTNOTE_COUNTER_ATTR,
    );
    if (!footnoteCounter) {
      return;
    }
    const target = SemanticFootnote.resolveSemanticFootnoteTarget(
      element,
      this.xmldoc.url,
      (reference) => this.xmldoc.getElement(reference),
    );
    if (target) {
      target.setAttribute(CssCascade.FOOTNOTE_COUNTER_ATTR, footnoteCounter);
    }
  }

  createPseudoelementShadow(
    nodeContext: Vtree.NodeContext,
    element: Element,
    isRoot: boolean,
    cascStyle: CssCascade.ElementStyle,
    computedStyle: { [key: string]: Css.Val },
    styler: CssStyler.AbstractStyler,
    context: Exprs.Context,
    parentShadow: Vtree.ShadowContext | null,
    subShadow: Vtree.ShadowContext | null,
  ): Vtree.ShadowContext | null {
    const pseudoMap = this.getPseudoMap(
      cascStyle,
      this.regionIds,
      this.isFootnote,
      nodeContext,
      context,
    );
    if (!pseudoMap) {
      return subShadow;
    }
    const addedNames: string[] = [];
    const root = PseudoElement.document.createElementNS(Base.NS.SHADOW, "root");
    let att = root;
    const suppressSemanticNoterefContent =
      SemanticFootnote.isSemanticFootnoteNoterefElement(element) &&
      !!pseudoMap["footnote-call"] &&
      Vtree.nonTrivialContent(
        (pseudoMap["footnote-call"]["content"] as CssCascade.CascadeValue)
          ?.value,
      );
    for (const name of PseudoElement.pseudoNames) {
      let elem: Element;
      if (name) {
        if (!pseudoMap[name]) {
          continue;
        }
        const isSemanticFootnote =
          SemanticFootnote.isSemanticFootnoteElement(element);
        const isSemanticFootnoteContentInclude =
          element.namespaceURI === Base.NS.SHADOW &&
          element.localName === "include" &&
          element.classList.contains("-vivliostyle-footnote-content");
        if (
          name == "footnote-call" &&
          !SemanticFootnote.isSemanticFootnoteNoterefElement(element)
        ) {
          continue;
        }
        if (
          name == "footnote-marker" &&
          !(
            isSemanticFootnote ||
            isSemanticFootnoteContentInclude ||
            (isRoot && this.isFootnote)
          ) &&
          !this.nodeContext?.pluginProps["nestedFootnoteDetached"] // (Issue #1352)
        ) {
          continue;
        }
        if (name.startsWith("first-")) {
          const display = computedStyle["display"];
          if (!display || display === Css.ident.inline) {
            continue;
          }
          const child = element.firstElementChild;
          if (child && Vtree.canIgnore(child.previousSibling)) {
            const childStyle = styler.getStyle(child, false);
            if (childStyle) {
              const childDisplay = CssCascade.getProp(childStyle, "display");
              if (
                childDisplay?.value &&
                childDisplay.value !== Css.ident.inline
              ) {
                continue;
              }
            }
          }
        }
        if (
          name === "before" ||
          name === "after" ||
          name === "footnote-call" ||
          name === "footnote-marker"
        ) {
          const content = pseudoMap[name]["content"] as CssCascade.CascadeValue;
          const allowFootnoteEmptyPseudo =
            (name === "before" || name === "after") &&
            ((computedStyle["float"] as Css.Val) === Css.ident.footnote ||
              isSemanticFootnote ||
              isSemanticFootnoteContentInclude) &&
            !!content;
          if (
            !content ||
            (!Vtree.nonTrivialContent(content.value) &&
              !allowFootnoteEmptyPseudo)
          ) {
            continue;
          }
        }
        addedNames.push(name);
        elem = PseudoElement.document.createElementNS(Base.NS.XHTML, "span");
        PseudoElement.setPseudoName(elem, name);
      } else {
        if (suppressSemanticNoterefContent) {
          continue;
        }
        elem = PseudoElement.document.createElementNS(
          Base.NS.SHADOW,
          "content",
        );
      }
      att.appendChild(elem);
      if (name.startsWith("first-")) {
        att = elem;
      }
    }
    if (!addedNames.length) {
      return subShadow;
    }
    const shadowStyler = new PseudoElement.PseudoelementStyler(
      element,
      cascStyle,
      styler,
      context,
      this.exprContentListener,
    );
    const pseudoShadow = new Vtree.ShadowContext(
      element,
      root,
      null,
      parentShadow,
      subShadow,
      Vtree.ShadowType.ROOTLESS,
      shadowStyler,
    );
    return subShadow || pseudoShadow;
  }

  getPseudoMap(
    cascStyle: CssCascade.ElementStyle,
    regionIds: string[] | null,
    isFootnote: boolean,
    nodeContext: Vtree.NodeContext,
    context: Exprs.Context,
  ): CssCascade.ElementStyleMap | null {
    const pseudoMap = CssCascade.getStyleMap(cascStyle, "_pseudos");
    if (!pseudoMap) {
      return null;
    }
    const computedPseudoStyleMap: CssCascade.ElementStyleMap = {};
    for (const key in pseudoMap) {
      const computedPseudoStyle: { [key: string]: CssCascade.CascadeValue } =
        (computedPseudoStyleMap[key] = {});
      CssCascade.mergeStyle(computedPseudoStyle, pseudoMap[key], context);
      CssCascade.mergeViewConditionalStyles(
        computedPseudoStyle,
        context,
        pseudoMap[key],
      );
      CssCascade.forEachStylesInRegion(
        pseudoMap[key],
        regionIds,
        isFootnote,
        (regionId, regionStyle) => {
          CssCascade.mergeStyle(computedPseudoStyle, regionStyle, context);
          CssCascade.forEachViewConditionalStyles(
            regionStyle,
            (viewConditionalStyles) => {
              CssCascade.mergeStyle(
                computedPseudoStyle,
                viewConditionalStyles,
                context,
              );
            },
          );
        },
      );
    }
    return computedPseudoStyleMap;
  }

  createRefShadow(
    href: string,
    type: Vtree.ShadowType,
    element: Element,
    parentShadow: Vtree.ShadowContext | null,
    subShadow: Vtree.ShadowContext | null,
  ): Task.Result<Vtree.ShadowContext | null> {
    const frame: Task.Frame<Vtree.ShadowContext | null> =
      Task.newFrame("createRefShadow");
    this.xmldoc.store.load(href).then((refDocParam) => {
      const refDoc = refDocParam;
      if (refDoc) {
        const refElement = refDoc.getElement(href);
        if (refElement) {
          const refStyler = this.stylerProducer.getStylerForDoc(refDoc);
          subShadow = new Vtree.ShadowContext(
            element,
            refElement,
            refDoc,
            parentShadow,
            subShadow,
            type,
            refStyler,
          );
        }
      }
      frame.finish(subShadow);
    });
    return frame.result();
  }

  createShadows(
    nodeContext: Vtree.NodeContext,
    element: Element,
    isRoot: boolean,
    cascStyle: CssCascade.ElementStyle,
    computedStyle: { [key: string]: Css.Val },
    styler: CssStyler.AbstractStyler,
    context: Exprs.Context,
    shadowContext: Vtree.ShadowContext | null,
  ): Task.Result<Vtree.ShadowContext | null> {
    const frame: Task.Frame<Vtree.ShadowContext | null> =
      Task.newFrame("createShadows");
    const shadow: Vtree.ShadowContext | null = null;
    const templateURLVal = computedStyle["template"];
    let cont: Task.Result<Vtree.ShadowContext | null>;
    if (
      templateURLVal instanceof Css.URL ||
      templateURLVal === Css.ident.footnote
    ) {
      if (
        templateURLVal !== Css.ident.footnote ||
        SemanticFootnote.shouldGenerateSemanticFootnote(
          element,
          this.xmldoc.url,
          (noteref) => this.xmldoc.getElementOffset(noteref),
          this.semanticFootnoteFirstRefOffsets,
          this.semanticFootnoteFirstRefOffsetsInitialized,
        )
      ) {
        const url =
          templateURLVal instanceof Css.URL
            ? templateURLVal.url
            : Base.resolveURL("user-agent.xml#footnote", Base.resourceBaseURL);
        cont = this.createRefShadow(
          url,
          Vtree.ShadowType.ROOTLESS,
          element,
          shadowContext,
          shadow,
        );
      } else {
        cont = Task.newResult(shadow);
      }
    } else {
      cont = Task.newResult(shadow);
    }
    cont.then((shadow) => {
      let cont1: Task.Result<Vtree.ShadowContext | null> | null = null;
      if (element.namespaceURI == Base.NS.SHADOW) {
        if (element.localName == "include") {
          let href = element.getAttribute("href");
          let xmldoc: XmlDoc.XMLDocHolder = this.xmldoc;
          if (href) {
            xmldoc = shadowContext?.xmldoc ?? this.xmldoc;
          } else if (shadowContext) {
            if (shadowContext.owner.namespaceURI == Base.NS.XHTML) {
              href = shadowContext.owner.getAttribute("href");
            } else {
              href = shadowContext.owner.getAttributeNS(Base.NS.XLINK, "href");
            }
            xmldoc = shadowContext.parentShadow?.xmldoc ?? this.xmldoc;
          }
          if (href) {
            href = Base.resolveReferenceURL(href, xmldoc.url);
            cont1 = this.createRefShadow(
              href,
              Vtree.ShadowType.ROOTED,
              element,
              shadowContext,
              shadow,
            );
          }
        }
      }
      if (cont1 == null) {
        cont1 = Task.newResult(shadow);
      }
      let cont2: Task.Result<Vtree.ShadowContext | null> | null = null;
      cont1.then((shadow) => {
        if (computedStyle["display"] === Css.ident.table_cell) {
          const url = Base.resolveURL(
            "user-agent.xml#table-cell",
            Base.resourceBaseURL,
          );
          cont2 = this.createRefShadow(
            url,
            Vtree.ShadowType.ROOTLESS,
            element,
            shadowContext,
            shadow,
          );
        } else {
          cont2 = Task.newResult(shadow);
        }
        cont2.then((shadow) => {
          shadow = this.createPseudoelementShadow(
            nodeContext,
            element,
            isRoot,
            cascStyle,
            computedStyle,
            styler,
            context,
            shadowContext,
            shadow,
          );
          frame.finish(shadow);
        });
      });
    });
    return frame.result();
  }

  /** @override */
  setViewRoot(viewRoot: Element, isFootnote: boolean) {
    this.viewRoot = viewRoot;
    this.isFootnote = isFootnote;
  }

  /**
   * @return used writing mode
   */
  computeStyle(
    inheritedWritingMode: string,
    rtl: boolean,
    style: CssCascade.ElementStyle,
    computedStyle: { [key: string]: Css.Val },
    cascadedMargins?: { [key: string]: CssCascade.CascadeValue },
  ): string {
    const context = this.context;
    const cascMap = CssCascade.flattenCascadedStyle(
      style,
      context,
      this.regionIds,
      this.isFootnote,
    );
    const isRoot = !this.nodeContext?.parent;
    if (isRoot) {
      // Ensure that writing-mode and direction are set on the root element,
      // except when the values are propagated from the body element. That
      // propagation is done on used values rather than computed values
      // (CSS Writing Modes spec), so it must not affect the root element's
      // computed style; otherwise the values would be inherited by the page
      // context (e.g., page margin boxes). (Issue #1122)
      const rootWritingMode = this.styler.rootStyle[
        "writing-mode"
      ] as CssCascade.CascadeValue;
      if (
        !cascMap["writing-mode"] &&
        rootWritingMode &&
        this.styler.bodyPropagatedStyle["writing-mode"] !== rootWritingMode
      ) {
        cascMap["writing-mode"] = rootWritingMode;
      }
      const rootDirection = this.styler.rootStyle[
        "direction"
      ] as CssCascade.CascadeValue;
      if (
        !cascMap["direction"] &&
        rootDirection &&
        this.styler.bodyPropagatedStyle["direction"] !== rootDirection
      ) {
        cascMap["direction"] = rootDirection;
      }
    }
    const verticalParent = inheritedWritingMode !== "horizontal-tb";
    const writingMode = CssCascade.resolveWritingMode(
      cascMap,
      context,
      inheritedWritingMode,
    );
    const vertical = writingMode !== "horizontal-tb";
    // When writing-mode is propagated from the body element to the root
    // element as a used value (CSS Writing Modes spec), the root element's
    // used writing mode is the propagated one although its computed style is
    // unchanged. Return the used value verticality for the root so that the
    // body element is not treated as a writing-mode change (which would
    // cause the vertical-in-horizontal treatment (Issue #1264), e.g.,
    // `inline-size: auto` becoming `max-content`). (Issue #1122 follow-up)
    let usedWritingMode = writingMode;
    if (isRoot && !vertical) {
      const rootWritingModeCasc = this.styler.rootStyle[
        "writing-mode"
      ] as CssCascade.CascadeValue;
      if (
        rootWritingModeCasc &&
        this.styler.bodyPropagatedStyle["writing-mode"] === rootWritingModeCasc
      ) {
        usedWritingMode = CssCascade.resolveWritingMode(
          { "writing-mode": rootWritingModeCasc },
          context,
          writingMode,
        );
      }
    }
    const verticalChanged = !isRoot && vertical !== verticalParent;
    // Only use the page-area fallback when the containing block does not
    // supply the orthogonal inline size. A definite size must remain under
    // browser control, including percentage resolution and available space
    // after margins. (Issue #1264)
    let useOrthogonalFallback = false;
    if (verticalChanged) {
      const inlineSizeName = vertical ? "height" : "width";
      const displays = new Map<HTMLElement, string>();
      const getDisplay = (element: HTMLElement): string => {
        let display = displays.get(element);
        if (display === undefined) {
          display = element.style?.display ?? "";
          if (
            /^(inline|ruby|ruby-base|ruby-text|ruby-base-container|ruby-text-container)$/.test(
              display,
            )
          ) {
            // Orthogonal inline boxes can be inline-blockified even though
            // their specified view style still says display:inline.
            display =
              element.ownerDocument.defaultView?.getComputedStyle(element)
                .display || display;
          }
          displays.set(element, display);
        }
        return display;
      };
      const findContainingBlock = (
        element: HTMLElement | null,
      ): HTMLElement | null => {
        // Boxless and non-atomic inline ancestors do not establish the
        // containing block for these block-level orthogonal flow roots.
        while (
          element &&
          element !== this.viewRoot &&
          /^(contents|inline|ruby|ruby-base|ruby-text|ruby-base-container|ruby-text-container)$/.test(
            getDisplay(element),
          )
        ) {
          element = element.parentElement;
        }
        return element === this.viewRoot ? null : element;
      };
      const containingBlock = findContainingBlock(
        this.nodeContext?.parent?.viewNode as HTMLElement,
      );
      const isFlexOrGrid = (element: HTMLElement | null): boolean =>
        !!element &&
        element !== this.viewRoot &&
        /^(inline-)?(grid|flex)$/.test(getDisplay(element));
      const hasLayoutSizing = (element: HTMLElement): boolean =>
        isFlexOrGrid(element) ||
        isFlexOrGrid(findContainingBlock(element.parentElement));
      // Cache all four states per element for this decision. Percentage and
      // ratio dependencies otherwise revisit the same ancestors exponentially.
      const definiteSizes = new Map<HTMLElement, Map<string, boolean>>();
      const hasDefiniteSize = (
        element: HTMLElement | null,
        dimension: "width" | "height",
        allowAspectRatio = true,
      ): boolean => {
        if (!element || element === this.viewRoot) {
          return false;
        }
        const key = `${dimension}:${allowAspectRatio}`;
        let cached = definiteSizes.get(element);
        const cachedValue = cached?.get(key);
        if (cachedValue !== undefined) {
          return cachedValue;
        }
        if (!cached) {
          cached = new Map();
          definiteSizes.set(element, cached);
        }
        const style = element.style;
        const size = style?.[dimension] ?? "";
        let definite = hasLayoutSizing(element);
        if (!definite && size.includes("%")) {
          // An unresolved percentage behaves as auto, so a preferred ratio
          // may still supply this axis from a definite opposite dimension.
          definite = hasDefiniteSize(
            findContainingBlock(element.parentElement),
            dimension,
          );
        } else if (
          !definite &&
          size &&
          !/^(auto|min-content|max-content|fit-content|stretch|inherit|initial|unset|revert)/.test(
            size,
          )
        ) {
          definite = true;
        }
        if (!definite && allowAspectRatio) {
          const components = (style?.aspectRatio ?? "")
            .replace(/\bauto\b/g, "")
            .trim()
            .split("/");
          const numerator = Number(components[0]);
          const denominator =
            components.length === 1 ? 1 : Number(components[1]);
          // Degenerate ratios behave as auto and supply no preferred size.
          definite =
            numerator > 0 &&
            Number.isFinite(numerator) &&
            denominator > 0 &&
            Number.isFinite(denominator) &&
            hasDefiniteSize(
              element,
              dimension === "height" ? "width" : "height",
              false,
            );
        }
        cached.set(key, definite);
        return definite;
      };
      // Flex/grid sizing only crosses the item/container boundary. An
      // ordinary auto-sized descendant does not acquire a definite basis
      // merely because some distant ancestor participates in that layout.
      useOrthogonalFallback = !hasDefiniteSize(containingBlock, inlineSizeName);
    }
    rtl = CssCascade.isRtl(cascMap, context, rtl);

    const transform = (
      name: string,
      cascVal: CssCascade.CascadeValue,
    ): Css.Val => {
      // The percent value of inline-size on vertical-in-horizontal or
      // horizontal-in-vertical block needs to be resolved against the
      // page area height or width. (Fix for issue #1264)
      let percentRef: number | undefined;
      if (useOrthogonalFallback) {
        if (vertical) {
          if (/^(min-|max-)?(height|inline-size)$/.test(name)) {
            percentRef = context.pageAreaHeight ?? undefined;
          }
        } else {
          if (/^(min-|max-)?(width|inline-size)$/.test(name)) {
            percentRef = context.pageAreaWidth ?? undefined;
          }
        }
      }
      let value = cascVal.evaluate(context, name, percentRef, vertical);
      if (name == "font-family" || name == "--viv-marker-font-family") {
        value = this.docFaces.filterFontFamily(value);
      }
      return value;
    };

    const isLeftPage = !!new Exprs.Named(
      (context as any).style.pageScope,
      "left-page",
    ).evaluate(context);

    CssCascade.convertToPhysical(
      cascMap,
      computedStyle,
      writingMode,
      rtl,
      isLeftPage,
      transform,
    );

    if (
      cascadedMargins &&
      computedStyle["display"] === Css.getName("ruby-text")
    ) {
      // Keep the winning margin's importance through the same physical/logical
      // resolution as its value, including shorthand, layers and page sides.
      const margins: { [key: string]: CssCascade.CascadeValue } = {};
      for (const name in cascMap) {
        if (name.startsWith("margin-")) {
          margins[name] = cascMap[name];
        }
      }
      CssCascade.convertToPhysical(
        margins,
        cascadedMargins,
        writingMode,
        rtl,
        isLeftPage,
        (_name, value) => value,
      );
    }

    if (Display.isRunning(computedStyle["position"])) {
      // Running elements
      computedStyle["position"] = Css.ident.fixed;
      computedStyle["visibility"] = Css.ident.hidden;
      return usedWritingMode;
    }

    // Compute values of display, position and float
    const position = computedStyle["position"] as Css.Ident;
    const float = computedStyle["float"];
    const display =
      (computedStyle["display"] as Css.Ident) ||
      (this.sourceNode &&
      (this.sourceNode as Element).namespaceURI === Base.NS.XHTML
        ? Css.ident.inline
        : undefined); // leave it to the browser for MathML and SVG
    const displayValues = Display.getComputedDisplayValue(
      display,
      position,
      float,
      this.sourceNode === this.xmldoc.root,
      !isRoot && writingMode !== inheritedWritingMode,
    );
    ["display", "position", "float"].forEach((name) => {
      if (displayValues[name]) {
        computedStyle[name] = displayValues[name];
      }
    });
    if (
      useOrthogonalFallback &&
      Display.isBlockLevel(computedStyle["display"])
    ) {
      // The auto value of inline-size on vertical-in-horizontal or
      // horizontal-in-vertical block is changed to max-content.
      // (Fix for issue #1264)
      const inlineSize = computedStyle[vertical ? "height" : "width"];
      if (!inlineSize || inlineSize === Css.ident.auto) {
        computedStyle[vertical ? "height" : "width"] = Css.ident.max_content;
      }
    }

    return usedWritingMode;
  }

  private inheritFromSourceParent(
    nodeContext: Vtree.NodeContext,
    elementStyle: CssCascade.ElementStyle,
  ): {
    lang: string | null;
    elementStyle: CssCascade.ElementStyle;
  } {
    let node: Node | null = nodeContext.sourceNode;
    const styles: CssCascade.ElementStyle[] = [];
    const styleLanguages: (string | null)[] = [];
    let lang: string | null = null;
    const isFootnoteContentInclude =
      node instanceof Element &&
      node.namespaceURI === Base.NS.SHADOW &&
      node.localName === "include" &&
      node.classList.contains("-vivliostyle-footnote-content");
    const shouldSkipAncestorMarkerProps =
      this.isFootnote && (!nodeContext.parent || isFootnoteContentInclude);

    // TODO: this is hacky. We need to recover the path through the shadow
    // trees, but we do not have the full shadow tree structure at this point.
    // This code handles coming out of the shadow trees, but does not go back in
    // (through shadow:content element).
    let shadowContext = nodeContext.shadowContext;

    // For semantic footnotes, the wrapper generated from
    // <s:include class="-vivliostyle-footnote-content"> should inherit from
    // the referenced footnote's source parent, not from the noteref owner.
    // (Issue #1770)
    if (
      node instanceof Element &&
      node.namespaceURI === Base.NS.SHADOW &&
      node.localName === "include" &&
      node.classList.contains("-vivliostyle-footnote-content")
    ) {
      const owner = shadowContext?.owner;
      if (
        owner instanceof Element &&
        SemanticFootnote.isSemanticFootnoteNoterefElement(owner)
      ) {
        const href =
          owner.getAttribute("href") ||
          owner.getAttributeNS(Base.NS.XLINK, "href");
        if (href) {
          const resolvedHref = Base.resolveReferenceURL(href, this.xmldoc.url);
          const target = this.xmldoc.getElement(resolvedHref);
          if (
            target &&
            SemanticFootnote.isSemanticFootnoteElement(target) &&
            target.parentNode?.nodeType === 1
          ) {
            node = target.parentNode;
            shadowContext = null;
          }
        }
      }
    }

    let steps = -1;
    while (node && node.nodeType == 1) {
      const shadow = shadowContext;
      const shadowRoot = shadow !== null && shadow.root == node;
      const semanticFootnoteRootInRootedShadow =
        shadowRoot &&
        shadow.type == Vtree.ShadowType.ROOTED &&
        SemanticFootnote.isSemanticFootnoteElement(node as Element);
      if (
        !shadowRoot ||
        shadow.type == Vtree.ShadowType.ROOTLESS ||
        semanticFootnoteRootInRootedShadow
      ) {
        const styler = shadow
          ? (shadow.styler as CssStyler.AbstractStyler)
          : this.styler;
        const nodeStyle = styler.getStyle(node as Element, false);
        styles.push(nodeStyle);
        const nodeLanguage = Base.getLangAttribute(node as Element);
        styleLanguages.push(nodeLanguage);
        lang = lang || nodeLanguage;
      }
      if (shadowRoot && !semanticFootnoteRootInRootedShadow) {
        node = shadow.owner;
        shadowContext = shadow.parentShadow;
      } else {
        node = node.parentNode;
        steps++;
      }
    }

    // When reconstructing inherited values for detached/transcluded nodes,
    // keep declarations on the current element (including region-specific
    // rules such as :footnote-content) authoritative over ancestor-derived
    // inherited values.
    const blockedInheritedByCurrent = new Set<string>();
    // Whether the current element's font size (including region rules) wins
    // over the inherited font size.
    let currentDeclaresFontSize = false;
    // Whether the current element's own cascaded style declares font-size.
    const fontSizeFromOwnStyle = !!(
      styles.length > 0 && CssCascade.getProp(styles[0], "font-size")
    );
    // Whether the current element's line height (including region rules) uses
    // the `lh` unit, which is resolved against the inherited line height.
    let currentDeclaresLineHeight = false;
    // Whether the current element's own cascaded style declares line-height.
    const lineHeightFromOwnStyle = !!(
      styles.length > 0 && CssCascade.getProp(styles[0], "line-height")
    );
    const flattenedCurrentStyle = styles.length
      ? CssCascade.flattenCascadedStyle(
          styles[0],
          this.context,
          this.regionIds,
          this.isFootnote,
        )
      : {};
    const sourceFontSize = styles.length
      ? CssCascade.getProp(styles[0], "font-size")
      : null;
    const winningFontSize = flattenedCurrentStyle["font-size"];
    const hasWinningFontSizeOverride =
      !!winningFontSize && winningFontSize !== sourceFontSize;
    const sourceFontWeight = styles.length
      ? CssCascade.getProp(styles[0], "font-weight")
      : null;
    const winningFontWeight = flattenedCurrentStyle["font-weight"];
    const winningRelativeFontWeight =
      winningFontWeight &&
      winningFontWeight !== sourceFontWeight &&
      (CssCascade.hasKeywordName(winningFontWeight.value, "bolder") ||
        CssCascade.hasKeywordName(winningFontWeight.value, "lighter"));
    if (styles.length > 0) {
      const currentFontSize = flattenedCurrentStyle["font-size"]?.evaluate(
        this.context,
        "font-size",
      );
      const fontSizeUsesLineHeight =
        !!currentFontSize && CssCascade.usesLineHeightUnit(currentFontSize);
      for (const name in flattenedCurrentStyle) {
        if (!CssCascade.isInherited(name)) {
          continue;
        }
        const value = flattenedCurrentStyle[name].evaluate(this.context, name);
        if (
          value &&
          // A CSS-wide keyword that comes from a custom property is not
          // canonicalized, so compare the names: `--x: INHERIT; color:
          // var(--x)` must not block the inherited value. (Review)
          !CssCascade.hasKeywordName(value, "inherit") &&
          !CssCascade.hasKeywordName(value, "unset") &&
          value !== Css.empty &&
          !Css.isRollbackValue(value)
        ) {
          if (name === "font-size") {
            // A relative font size (em/%/lh units, a calc() of them, or the
            // relative keywords "larger"/"smaller") is resolved against the
            // inherited value, so the inherited font size must still be
            // accumulated. It is only the priority of the accumulated value
            // that must not beat the declaration; see below. (Issue #2174)
            currentDeclaresFontSize = true;
          } else if (
            // An `lh` unit of a line height refers to the line height of the
            // parent, so the inherited line height must still be accumulated as
            // well. The declaration replaces the accumulated value, but only
            // the priority of the accumulated value is lowered below when the
            // declaration is not in the element's own cascaded style. (Review)
            name === "line-height" &&
            // An own line-height must not block the source parent's value
            // while resolving `font-size: 1lh`. Font size is processed first,
            // then this element's line-height replaces the inherited value.
            (fontSizeUsesLineHeight ||
              CssCascade.usesLineHeightUnit(value) ||
              // A line height that the browser rejects is inherited as well,
              // so the inherited line height must still be accumulated for a
              // descendant that resolves the `lh` unit. (Review)
              CssCascade.isInvalidLineHeight(this.context, value))
          ) {
            currentDeclaresLineHeight = true;
          } else if (!fontMetricProperties.includes(name)) {
            // Font traits of the source parent are needed to resolve this
            // element's font-relative font-size before its own traits apply.
            blockedInheritedByCurrent.add(name);
          }
        }
      }
    }

    const isRoot = steps === 0;
    const fontSize = this.context.queryUnitSize("em", isRoot);
    const props = {
      "font-size": new CssCascade.CascadeValue(
        new Css.Numeric(fontSize, "px"),
        0,
      ),
      // The initial value that every source ancestor chain starts from, so
      // that detached content inherits `normal` from a source that declares no
      // line-height.
      "line-height": new CssCascade.CascadeValue(Css.ident.normal, 0),
    } as CssCascade.ElementStyle;
    const inheritanceVisitor = new CssCascade.InheritanceVisitor(
      props,
      this.context,
    );
    let inheritedLanguage = this.context.fontLanguage;
    for (let i = styles.length - 1; i >= 0; --i) {
      let style = styles[i];
      // Ancestors use their own view conditions, without the detached child's
      // region rules. Their computed font settings form the child's metrics.
      // Without view-conditional styles, an ancestor's flattened style is its
      // own style, so it is not flattened (this runs for every ancestor).
      const fontStyle =
        i === 0
          ? flattenedCurrentStyle
          : (style["_viewConditionalStyles"] as unknown[] | undefined)?.length
            ? CssCascade.flattenCascadedStyle(style, this.context, null, false)
            : null;
      // The current element's font-size still uses the source-parent basis;
      // its winning region/view traits apply after font-size is resolved.
      const fontProperties =
        i === 0
          ? fontMetricProperties
          : ["font-size", "line-height", ...fontMetricProperties];
      if (fontStyle) {
        for (const name of fontProperties) {
          if (fontStyle[name] && fontStyle[name] !== style[name]) {
            // Copy the style only when a value differs: it is only read below.
            if (style === styles[i]) {
              style = { ...styles[i] };
            }
            style[name] = fontStyle[name];
          }
        }
      }
      const currentLanguage = styleLanguages[i] ?? inheritedLanguage;
      inheritanceVisitor.fontLanguage = inheritedLanguage;
      const propList: string[] = [];
      for (const propName in style) {
        if (CssCascade.isInherited(propName)) {
          propList.push(propName);
        }
      }
      propList.sort(Css.processingOrderFn);
      let fontSize: Css.Val | undefined;
      let lineHeight: Css.Val | undefined;
      // The font size of the level above, which a relative line height that was
      // accumulated so far is resolved against: the font-relative units of a
      // line height refer to the parent's metrics, and this level's own font
      // size has not been applied here yet. (Review)
      const parentFontSize = inheritanceVisitor.getInheritedFontSize();
      // Capture normal before this level changes the accumulated font traits.
      const declaredLineHeight = CssCascade.getProp(
        style,
        "line-height",
      )?.value;
      const parentLineHeight =
        declaredLineHeight && CssCascade.usesLineHeightUnit(declaredLineHeight)
          ? inheritanceVisitor.getInheritedLineHeight(parentFontSize)
          : null;

      for (const name of propList) {
        if (
          i > 0 &&
          shouldSkipAncestorMarkerProps &&
          name.startsWith("--viv-marker-")
        ) {
          continue;
        }
        if (i > 0 && blockedInheritedByCurrent.has(name)) {
          continue;
        }
        inheritanceVisitor.fontLanguage =
          name === "font-size" ? inheritedLanguage : currentLanguage;
        inheritanceVisitor.setPropName(name);
        const prop = CssCascade.getProp(style, name);
        let prop1 = prop;
        if (CssCascade.hasKeywordName(prop.value, "initial")) {
          // `initial` means use the CSS initial value, not inherit from
          // ancestor. This is needed for `all: initial` to work on elements
          // detached from their source parent (e.g. footnotes). (Issue #1696)
          if (name === "font-size") {
            // The initial font size is materialized as a length, because it is
            // the base of the dependent values of the element, e.g. an `em`
            // unit of another declaration: `font-size: initial` is the (medium)
            // initial font size. (Issue #2174 follow-up)
            props[name] = prop.withValue(
              new Css.Numeric(this.context.initialFontSize, "px"),
            );
          } else {
            // The keyword itself is kept as the accumulated value. Removing it
            // would make the detached element inherit from the synthetic view
            // parent instead of using the initial value, e.g.
            // `color: initial` under a footnote area that inherits the color
            // of the page would be rendered in that color instead of the
            // initial one. (Review)
            props[name] = prop;
          }
        } else if (
          // The keywords are compared by name, because a keyword that comes
          // from a custom property is not canonicalized, e.g.
          // `--x: INHERIT; color: var(--x)`. (Review)
          CssCascade.hasKeywordName(prop.value, "inherit") ||
          // `unset` is the same as `inherit` for the inherited properties that
          // are processed here.
          CssCascade.hasKeywordName(prop.value, "unset")
        ) {
          const inherited = CssCascade.getProp(props, name);
          if (inherited) {
            // `inherit` is the inherited value. Materializing it with the
            // priority of this declaration lets it compete with a declaration
            // that is not in the element's own cascaded style, e.g. a region
            // rule, as any other declaration of the element would. (Issue
            // #2174)
            props[name] = prop.withValue(inherited.value);
          } else {
            // No source ancestor declares the property, so the value that the
            // element inherits is the initial one. Materializing `initial`
            // instead of leaving the declaration out makes the generated node
            // use that value rather than the one of the synthetic view parent,
            // e.g. a `color: inherit` footnote under a region rule such as
            // `:footnote-content { color: red }`. (Review)
            props[name] = prop.withValue(Css.ident.initial);
          }
        } else if (!Css.isDefaultingValue(prop.value)) {
          if (
            i === 0 &&
            name !== "font-size" &&
            (hasWinningFontSizeOverride || winningRelativeFontWeight) &&
            hasCurrentFontRelativeLength(prop.value)
          ) {
            // The browser applies the winning region/view font-size or
            // relative font-weight after this walk. Leave dependent units
            // intact so they resolve against those final font metrics,
            // including relative region sizes against the rendering parent.
            prop1 = prop;
            if (
              name === "line-height" &&
              CssCascade.usesLineHeightUnit(prop.value)
            ) {
              const inheritedLineHeight = parentLineHeight;
              if (inheritedLineHeight != null) {
                // Only current-font terms are deferred. lh in line-height
                // still refers to the source parent's line height.
                prop1 = prop.filterValue(
                  new CssCascade.LineHeightUnitReplacer(inheritedLineHeight),
                );
              }
            }
          } else if (
            name === "font-size" &&
            i === styles.length - 1 &&
            this.context.isRelativeRootFontSize &&
            this.context.rootFontSize != null
          ) {
            // Fix for issue #608, #549
            prop1 = prop.withValue(
              new Css.Numeric(this.context.rootFontSize, "px"),
            );
          } else if (
            name === "line-height" &&
            i === styles.length - 1 &&
            ((prop.value instanceof Css.Numeric &&
              (prop.value.unit === "lh" || prop.value.unit === "rlh")) ||
              this.context.isRootLineHeightFromRelativeCalc)
          ) {
            // line-height with lh or rlh unit on root element
            prop1 = prop.withValue(
              new Css.Numeric(this.context.rootLineHeight, "px"),
            );
          } else if (
            name === "line-height" &&
            prop.value instanceof Css.Numeric &&
            prop.value.unit === "lh"
          ) {
            // line-height with an lh unit, which refers to the line height of
            // the parent. The line height that was accumulated for the parent
            // is used for the current element and for a source ancestor alike:
            // on detached content the rendered parent is the synthetic one
            // (e.g. the footnote area), whose line height is not the one that
            // the element inherits in the source document. Resolved level by
            // level, a detached descendant that resolves an lh unit of its own
            // finds a length instead of falling back to the root line height. A
            // `font-size` with an lh unit is left to the inheritance visitor,
            // which resolves it against the line height that was accumulated
            // for the source parent. (Issue #2174 follow-up, Review)
            const lhUnitSize = parentLineHeight;
            if (lhUnitSize != null) {
              prop1 = prop.withValue(
                new Css.Numeric(prop.value.num * lhUnitSize, "px"),
              );
            }
          } else if (
            name === "line-height" &&
            prop.value instanceof Css.Func &&
            CssCascade.usesLineHeightUnit(prop.value)
          ) {
            // A computed line height whose `lh` unit refers to the line height
            // of the parent, e.g. `line-height: calc(1lh + 10px)`. It is
            // resolved here, level by level, against the line height that was
            // accumulated for the parent so far, so that the unit does not
            // fall back to the preferred line height when a descendant
            // resolves its own `lh` unit. (Review)
            const inheritedLineHeight = parentLineHeight;
            const inheritedFontSize = inheritanceVisitor.getInheritedFontSize();
            if (inheritedLineHeight != null && inheritedFontSize != null) {
              const px = CssCascade.resolveLineHeightValueToPx(
                this.context,
                prop.value,
                inheritedFontSize,
                inheritedLineHeight,
                {
                  ...CssCascade.getFontMetricProperties(props, this.context),
                  lang: currentLanguage || "",
                },
              );
              if (px != null) {
                prop1 = prop.withValue(new Css.Numeric(px, "px"));
              }
            }
          } else if (!Css.isCustomPropName(name)) {
            prop1 = prop.filterValue(inheritanceVisitor);
          }

          if (name === "font-weight") {
            // A weight that a var() substitution made invalid is rejected by
            // the browser, which keeps the inherited weight, so the accumulated
            // weight must not be replaced by it: a relative keyword
            // (`bolder`/`lighter`) of a detached descendant is resolved against
            // the inherited weight. (Review)
            if (CssCascade.isInvalidFontWeight(this.context, prop1.value)) {
              const inheritedFontWeight = CssCascade.getProp(props, name);
              if (inheritedFontWeight) {
                prop1 = prop1.withValue(inheritedFontWeight.value);
              }
            }
          }

          if (name === "line-height") {
            // A line height that a var() substitution made invalid is rejected
            // by the browser, which keeps the inherited line height, so the
            // accumulated value must not be replaced by it: a descendant that
            // resolves the `lh` unit (`font-size: 1lh`) then uses the line
            // height it inherits instead of the preferred line height.
            // (Review)
            if (CssCascade.isInvalidLineHeight(this.context, prop1.value)) {
              const inheritedLineHeight = CssCascade.getProp(props, name);
              if (inheritedLineHeight) {
                prop1 = prop1.withValue(inheritedLineHeight.value);
              }
            }
          }

          if (name === "font-size") {
            // `initial`, `inherit` and `unset` are handled above and the
            // keywords are resolved by the visitor, so every other value is
            // either a length or invalid. An invalid one, e.g. a unitless
            // number, an unknown identifier or a negative length that a var()
            // substitution put into the declaration, is rejected by the
            // browser, which inherits the parent font size instead; keep the
            // value that was accumulated for the source parent, so that the
            // dependent values of the element are not resolved against the
            // initial font size. (Review)
            if (prop1.value instanceof Css.Numeric && prop1.value.num < 0) {
              // `font-size` has a non-negative computed-value range, so a
              // negative value is clamped to zero. A negative literal length
              // is invalid instead and is turned into `unset` by the cascade,
              // which the walk materializes as the inherited font size.
              prop1 = prop1.withValue(new Css.Numeric(0, "px"));
            } else if (
              CssCascade.resolveFontSizeValueToPx(this.context, prop1.value) ==
                null &&
              // A valid value that cannot be resolved here, e.g. the `math`
              // keyword or a unit that only the browser resolves, is preserved
              // instead of being replaced by the inherited value. (Review)
              !CssCascade.isValidUnresolvedFontSize(this.context, prop1.value)
            ) {
              const inheritedFontSize = CssCascade.getProp(props, name);
              if (inheritedFontSize) {
                prop1 = prop1.withValue(inheritedFontSize.value);
              }
            }
          }

          if (name === "font-size") {
            fontSize = prop1.value;
          } else if (name === "line-height") {
            lineHeight = prop1.value;
          }

          props[name] = prop1;
        }
      }
      inheritedLanguage = currentLanguage;
    }
    if (currentDeclaresFontSize && !fontSizeFromOwnStyle) {
      const inheritedFontSize = props["font-size"] as
        CssCascade.CascadeValue | undefined;
      // The font size comes from a declaration that is not in the element's
      // own cascaded style, e.g. a region rule such as
      // `aside[role="doc-footnote"]:footnote-content { font-size: 0.9em }`.
      // Such a declaration replaces the inherited value, so the inherited
      // value is kept only as a low-priority placeholder (the same priority
      // as the initial value the walk starts with). Only the priority is
      // affected: a declaration of a region rule resolves in the context that
      // it is rendered in, as the styles of the `@page` margin boxes do and as
      // the default `font-size: 0.9em` of Vivliostyle's own footnote styles
      // relies on; it is not resolved against the source parent, unlike a
      // declaration of the element itself. (Issue #2174)
      if (inheritedFontSize) {
        props["font-size"] = new CssCascade.CascadeValue(
          inheritedFontSize.value,
          0,
        );
      }
    }
    if (currentDeclaresLineHeight && !lineHeightFromOwnStyle) {
      const inheritedLineHeight = props["line-height"] as
        CssCascade.CascadeValue | undefined;
      // A line height that uses the `lh` unit needs the inherited line height to
      // resolve the unit, but when the declaration comes from outside the
      // element's own cascaded style, e.g. a region rule such as
      // `aside[role="doc-footnote"]:footnote-content { line-height: 1lh }`, the
      // inherited value is kept only as a low-priority placeholder, so that it
      // cannot beat the declaration of the region rule (an `!important` value
      // of an ancestor would otherwise win). Only the priority is affected: as
      // for the font size above, the declaration of the region rule resolves in
      // the context that it is rendered in. (Review)
      if (inheritedLineHeight) {
        props["line-height"] = new CssCascade.CascadeValue(
          inheritedLineHeight.value,
          0,
        );
      }
    }
    // Ancestor traits were needed for source-parent font-size calculations.
    // Their priorities must not compete with declarations on this element,
    // including region and view-conditional rules merged after this walk.
    for (const name of fontMetricProperties) {
      const inherited = CssCascade.getProp(props, name);
      if (inherited && !CssCascade.getProp(styles[0], name)) {
        props[name] = new CssCascade.CascadeValue(inherited.value, 0);
      }
    }
    for (const sname in elementStyle) {
      if (!CssCascade.isInherited(sname)) {
        props[sname] = elementStyle[sname];
      }
    }
    return { lang, elementStyle: props };
  }

  resolveURL(url: string): string {
    url = Base.resolveURL(url, this.xmldoc.url);
    return this.fallbackMap[url] || url;
  }

  inheritLangAttribute(nodeContext: Vtree.NodeContext) {
    nodeContext.lang =
      Base.getLangAttribute(nodeContext.sourceNode as Element) ||
      (nodeContext.parent && nodeContext.parent.lang) ||
      nodeContext.lang;
  }

  transferPolyfilledInheritedProps(
    nodeContext: Vtree.NodeContext,
    computedStyle: { [key: string]: Css.Val },
  ) {
    const polyfilledInheritedProps =
      CssCascade.getPolyfilledInheritedProps().filter(
        (name) => computedStyle[name],
      );
    if (polyfilledInheritedProps.length) {
      let props = nodeContext.inheritedProps;
      if (nodeContext.parent) {
        props = nodeContext.inheritedProps = {};
        for (const n in nodeContext.parent.inheritedProps) {
          props[n] = nodeContext.parent.inheritedProps[n];
        }
      }
      polyfilledInheritedProps.forEach((name) => {
        const value = computedStyle[name];
        if (value) {
          if (Css.isDefaultingValue(value)) {
            if (value === Css.ident.initial) {
              props[name] = undefined;
            }
          } else if (value instanceof Css.Int) {
            props[name] = (value as Css.Int).num;
          } else if (value instanceof Css.Ident) {
            props[name] = (value as Css.Ident).name;
          } else if (value instanceof Css.Numeric) {
            const numericVal = value as Css.Numeric;
            switch (numericVal.unit) {
              case "dpi":
              case "dpcm":
              case "dppx":
                props[name] =
                  numericVal.num * Exprs.defaultUnitSizes[numericVal.unit];
                break;
              default:
                props[name] = value;
            }
          } else {
            props[name] = value;
          }
          if (!["widows", "orphans"].includes(name)) {
            // Note: widows and orphans are polyfilled for page and
            // root multi-column, but they should be left to the browser
            // for multi-column boxes inside body. (Issue #1182)
            delete computedStyle[name];
          }
        }
      });
    }
  }

  resolveFormattingContext(
    nodeContext: Vtree.NodeContext,
    firstTime: boolean,
    display: Css.Val | null | undefined,
    position: Css.Ident | null | undefined,
    float: Css.Val | null | undefined,
    isRoot: boolean,
  ) {
    const hooks: Plugin.ResolveFormattingContextHook[] = Plugin.getHooksForName(
      Plugin.HOOKS.RESOLVE_FORMATTING_CONTEXT,
    );
    for (let i = 0; i < hooks.length; i++) {
      const formattingContext = hooks[i](
        nodeContext,
        firstTime,
        display,
        position,
        float,
        isRoot,
      );
      if (formattingContext) {
        nodeContext.formattingContext = formattingContext;
        return;
      }
    }
  }

  /**
   * @return holding true if children should be processed
   */
  private createElementView(
    nodeContext: Vtree.NodeContext,
    firstTime: boolean,
  ): Task.Result<boolean> {
    let needToProcessChildren = true;
    const frame: Task.Frame<boolean> = Task.newFrame("createElementView");

    // Figure out element's styles
    let element = this.sourceNode as Element;
    const styler = nodeContext.shadowContext
      ? nodeContext.shadowContext.styler
      : this.styler;
    let elementStyle = styler.getStyle(element, false);
    this.syncSemanticFootnoteCounterToTarget(element);
    if (!nodeContext.shadowContext) {
      const offset = this.xmldoc.getElementOffset(element);
      Matchers.NthFragmentMatcher.registerFragmentIndex(
        offset,
        nodeContext.fragmentIndex,
        0,
      );
    }
    const computedStyle: { [key: string]: Css.Val } = {};
    const cascadedMargins: { [key: string]: CssCascade.CascadeValue } = {};
    const semanticFootnoteStyleAccess: SemanticFootnote.SemanticFootnoteStyleAccess =
      {
        getStyle: (target) => this.styler.getStyle(target, false),
        getProp: (style, propName) =>
          style ? CssCascade.getProp(style, propName) : null,
        getStyleMap: (style, mapName) => CssCascade.getStyleMap(style, mapName),
        getMutableStyleMap: (style, mapName) =>
          CssCascade.getMutableStyleMap(style, mapName),
        createCascadeValue: (value, priority) =>
          new CssCascade.CascadeValue(value, priority),
        filterFootnoteMarkerContent: (content, target) =>
          content.filterValue(
            new CssCascade.ContentPropVisitor(
              this.styler.cascade,
              target,
              this.styler.cascade.counterResolver,
              this.styler.elementWindow.currentStyle,
              "footnote-marker",
            ),
          ).value,
      };
    if (!nodeContext.parent) {
      const inheritedValues = this.inheritFromSourceParent(
        nodeContext,
        elementStyle,
      );
      elementStyle = inheritedValues.elementStyle;
      nodeContext.lang = inheritedValues.lang;
    }
    const positionCV: CssCascade.CascadeValue = elementStyle["position"];
    const floatCV: CssCascade.CascadeValue = elementStyle["float"];
    const floatReferenceCV: CssCascade.CascadeValue =
      elementStyle["float-reference"];
    const floatReference =
      floatCV &&
      !Css.isDefaultingValue(floatCV.value) &&
      floatCV.value !== Css.ident.none &&
      floatReferenceCV &&
      !Css.isDefaultingValue(floatReferenceCV.value)
        ? PageFloats.floatReferenceOf(floatReferenceCV.value.toString())
        : null;
    const isSemanticFootnoteInRootedShadow =
      !!nodeContext.shadowContext &&
      nodeContext.shadowContext.type === Vtree.ShadowType.ROOTED &&
      SemanticFootnote.isSemanticFootnoteElement(element);
    if (
      nodeContext.parent &&
      (Display.isRunning(positionCV?.value) ||
        (floatReference && PageFloats.isPageFloat(floatReference)) ||
        isSemanticFootnoteInRootedShadow)
    ) {
      // Detached or transcluded content should inherit from the source tree,
      // not from the synthetic view parent.
      const inheritedValues = this.inheritFromSourceParent(
        nodeContext,
        elementStyle,
      );
      elementStyle = inheritedValues.elementStyle;
      nodeContext.lang = inheritedValues.lang;
    }
    elementStyle = SemanticFootnote.mergeSemanticFootnoteIncludeStyle(
      element,
      elementStyle,
      nodeContext.shadowContext,
      this.xmldoc.url,
      (reference) => this.xmldoc.getElement(reference),
      CssCascade.FOOTNOTE_COUNTER_ATTR,
      semanticFootnoteStyleAccess,
    );
    elementStyle = SemanticFootnote.mergeSemanticFootnoteRootStyle(
      element,
      elementStyle,
      nodeContext.shadowContext,
      this.context,
      semanticFootnoteStyleAccess,
    );
    nodeContext.writingMode = this.computeStyle(
      nodeContext.writingMode,
      nodeContext.direction === "rtl",
      elementStyle,
      computedStyle,
      cascadedMargins,
    );
    nodeContext.vertical = nodeContext.writingMode !== "horizontal-tb";
    if (
      floatReference &&
      PageFloats.isPageFloat(floatReference) &&
      computedStyle["display"] === Css.ident.block
    ) {
      // Fix page float margin collapsing issue (Issue #1282)
      computedStyle["display"] = Css.ident.flow_root;
    }
    styler.processContent(element, computedStyle, nodeContext);
    this.transferPolyfilledInheritedProps(nodeContext, computedStyle);
    this.inheritLangAttribute(nodeContext);
    if (computedStyle["direction"]) {
      nodeContext.direction = computedStyle["direction"].toString();
    }

    // Sort out the properties
    const flow = computedStyle["flow-into"];
    if (flow && flow.toString() != this.flowName) {
      // foreign flow, don't create a view
      frame.finish(false);
      return frame.result();
    }
    if (
      Scripts.allowScripts &&
      element.localName === "script" &&
      element.namespaceURI === Base.NS.XHTML
    ) {
      Scripts.loadScript(
        element as HTMLScriptElement,
        this.viewport.window,
      ).thenFinish(frame);
      return frame.result();
    }

    let display: Css.Val | null | undefined = computedStyle["display"];

    if (
      SemanticFootnote.isSemanticFootnoteElement(element) &&
      !isSemanticFootnoteInRootedShadow &&
      element.hasAttribute(SemanticFootnote.SEMANTIC_FOOTNOTE_REFERENCED_ATTR)
    ) {
      display = computedStyle["display"] = Css.ident.none;
    }

    if (Css.isDefaultingValue(display)) {
      if (display === Css.ident.initial || display === Css.ident.unset) {
        display = Css.ident.inline;
      } else if (display === Css.ident.inherit) {
        display = nodeContext.parent?.display
          ? Css.getName(nodeContext.parent.display)
          : null;
      } else {
        display = null;
      }
    }
    if (display === Css.ident.none) {
      // no content
      frame.finish(false);
      return frame.result();
    }
    const isRoot = nodeContext.parent == null;
    const blockSize = computedStyle[nodeContext.vertical ? "width" : "height"];
    // Issue #1999: fixed-size grid boxes used as synthetic page references
    // must stay whole. Once fragmented, continuation fragments lose the block
    // size and later reference pages collapse.
    const isFixedSizeGridContainer =
      (display === Css.ident.grid || display === Css.ident.inline_grid) &&
      !!blockSize &&
      !(blockSize === Css.ident.auto || Css.isDefaultingValue(blockSize));
    nodeContext.flexContainer =
      display === Css.ident.flex || isFixedSizeGridContainer;
    this.createShadows(
      nodeContext,
      element,
      isRoot,
      elementStyle,
      computedStyle,
      styler,
      this.context,
      nodeContext.shadowContext,
    ).then((shadowParam) => {
      nodeContext.nodeShadow = shadowParam;
      const position = computedStyle["position"] as Css.Ident;
      let floatSide: Css.Val | null = computedStyle["float"];
      let clearSide: Css.Ident | null = computedStyle["clear"] as Css.Ident;
      const writingMode = Css.getName(nodeContext.writingMode);
      const parentWritingMode = Css.getName(
        nodeContext.parent?.writingMode ?? nodeContext.writingMode,
      );
      const isFlowRoot = Display.isFlowRoot(element);
      const columnCount = computedStyle["column-count"];
      const columnWidth = computedStyle["column-width"];
      const isMultiColumn =
        (columnCount &&
          columnCount !== Css.ident.auto &&
          !Css.isDefaultingValue(columnCount)) ||
        (columnWidth &&
          columnWidth !== Css.ident.auto &&
          !Css.isDefaultingValue(columnWidth));
      nodeContext.establishesBFC = Display.establishesBFC(
        display,
        position,
        floatSide,
        computedStyle["overflow"] as Css.Ident,
        writingMode,
        parentWritingMode,
        isMultiColumn || isFlowRoot,
      );
      nodeContext.containingBlockForAbsolute =
        Display.establishesCBForAbsolute(position);
      if (
        nodeContext.isInsideBFC() &&
        floatSide !== Css.ident.footnote &&
        !(floatReference && PageFloats.isPageFloat(floatReference))
      ) {
        // When the element is already inside a block formatting context
        // (except one from the root), float and clear can be controlled by
        // the browser and we don't need to care.
        floatSide = null;
        clearSide = null;
      }
      let footnoteDisplay: Css.Val | null = null;
      let usesOutsideFootnoteMarker = false;
      const isFootnoteBodyInFootnoteArea =
        this.isFootnote &&
        (!nodeContext.parent ||
          nodeContext.pluginProps["nestedFootnoteDetached"] ||
          isSemanticFootnoteInRootedShadow);
      const semanticFootnoteStyle =
        SemanticFootnote.getSemanticFootnoteStyleState(
          element,
          nodeContext.shadowContext,
          semanticFootnoteStyleAccess,
        );
      SemanticFootnote.refreshSemanticFootnoteMarkerContent(
        semanticFootnoteStyle.sourceStyle,
        computedStyle,
        this.context,
      );
      footnoteDisplay = semanticFootnoteStyle.footnoteDisplay;

      const floatSideName =
        floatSide instanceof Css.SpaceList ||
        floatSide === Css.ident.left ||
        floatSide === Css.ident.right ||
        floatSide === Css.ident.top ||
        floatSide === Css.ident.bottom ||
        floatSide === Css.ident.inline_start ||
        floatSide === Css.ident.inline_end ||
        floatSide === Css.ident.block_start ||
        floatSide === Css.ident.block_end ||
        floatSide === Css.ident.snap_block ||
        floatSide === Css.ident.snap_inline ||
        floatSide === Css.ident.inside ||
        floatSide === Css.ident.outside ||
        floatSide === Css.ident.footnote
          ? floatSide.toString()
          : null;
      let floating = floatSideName !== null;
      if (floatSide) {
        // Don't want to set it in view DOM CSS.
        delete computedStyle["float"];
        if (floatSide === Css.ident.footnote) {
          footnoteDisplay =
            computedStyle["footnote-display"] || footnoteDisplay;
          usesOutsideFootnoteMarker = !!computedStyle["--viv-marker-content"];
          if (isFootnoteBodyInFootnoteArea) {
            // Root of the footnote body being rendered in footnote area.
            // Nested footnote inside a footnote area: render as a detached block
            // entry, with call marker handled separately. (Issue #1352)
            floating = false;
            const footnoteDisplayName = footnoteDisplay?.toString();
            if (footnoteDisplayName) {
              computedStyle["--viv-footnote-display"] =
                Css.getName(footnoteDisplayName);
            }
            display = usesOutsideFootnoteMarker
              ? Css.getName("list-item")
              : footnoteDisplayName === "inline" ||
                  footnoteDisplayName === "compact"
                ? Css.ident.inline
                : Css.ident.block;
            computedStyle["display"] = display;
          } else {
            // Footnote body in main flow (not yet in footnote area):
            // render inline (only the call marker is visible), but keep
            // the blockified display variable so nodeContext.inline stays
            // false — layout needs this to collect the float properly.
            computedStyle["display"] = Css.ident.inline;
          }
        }
      }
      if (
        isFootnoteBodyInFootnoteArea &&
        SemanticFootnote.isSemanticFootnoteElement(element)
      ) {
        usesOutsideFootnoteMarker =
          usesOutsideFootnoteMarker || !!computedStyle["--viv-marker-content"];
        const footnoteDisplayName = footnoteDisplay?.toString();
        if (footnoteDisplayName) {
          computedStyle["--viv-footnote-display"] =
            Css.getName(footnoteDisplayName);
        }
        if (usesOutsideFootnoteMarker) {
          display = computedStyle["display"] = Css.getName("list-item");
        }
        if (
          !usesOutsideFootnoteMarker &&
          (footnoteDisplayName === "inline" ||
            footnoteDisplayName === "compact")
        ) {
          display = computedStyle["display"] = Css.ident.inline;
        }
      }
      if (clearSide) {
        if (clearSide === Css.ident.inherit) {
          if (nodeContext.parent && nodeContext.parent.clearSide) {
            clearSide = Css.getName(nodeContext.parent.clearSide);
          }
        }
        if (
          clearSide === Css.ident.left ||
          clearSide === Css.ident.right ||
          clearSide === Css.ident.top ||
          clearSide === Css.ident.bottom ||
          clearSide === Css.ident.inline_start ||
          clearSide === Css.ident.inline_end ||
          clearSide === Css.ident.block_start ||
          clearSide === Css.ident.block_end ||
          clearSide === Css.ident.inside ||
          clearSide === Css.ident.outside ||
          clearSide === Css.ident.both ||
          clearSide === Css.ident.all ||
          clearSide === Css.ident.same ||
          clearSide === Css.ident.column ||
          clearSide === Css.ident.region ||
          clearSide === Css.ident.page
        ) {
          delete computedStyle["clear"];
          if (
            floating ||
            (computedStyle["display"] &&
              computedStyle["display"] != Css.ident.inline)
          ) {
            nodeContext.clearSide = clearSide.toString();
          }
        }
      }
      const isListItem =
        Display.isListItem(display) &&
        !!(
          computedStyle["ua-list-item-count"] ||
          computedStyle["--viv-marker-content"]
        );
      const breakInside = computedStyle["break-inside"];
      if (
        floating ||
        (breakInside &&
          !Css.isDefaultingValue(breakInside) &&
          breakInside !== Css.ident.auto)
      ) {
        nodeContext.breakPenalty++;
      }
      if (
        display &&
        display !== Css.ident.inline &&
        Display.isInlineLevel(display)
      ) {
        // Don't break inside ruby, inline-block, etc.
        nodeContext.breakPenalty++;
      }
      // An absolutely positioned child is out of flow even when its display
      // is block-level (for example, flex). Keep it in the inline parent's
      // layout traversal so a page break cannot separate them. (#2162)
      const isAbsolutelyPositionedInInline =
        position === Css.ident.absolute && !!nodeContext.parent?.inline;
      nodeContext.inline =
        (!floating && !display) ||
        Display.isInlineLevel(display) ||
        Display.isRubyInternalDisplay(display) ||
        isAbsolutelyPositionedInInline;
      nodeContext.display = display ? display.toString() : "inline";
      nodeContext.floatSide = floating ? floatSideName : null;
      nodeContext.floatReference =
        floatReference || PageFloats.FloatReference.INLINE;
      const floatMinWrapBlock = computedStyle["float-min-wrap-block"];
      nodeContext.floatMinWrapBlock =
        floatMinWrapBlock && !Css.isDefaultingValue(floatMinWrapBlock)
          ? (floatMinWrapBlock as Css.Numeric)
          : null;

      // Leaves handling of multicol specified on non-root/body elements to the browser
      const insideNonRootMultiColumn =
        this.isInsideNonRootMultiColumn(nodeContext);

      const columnSpan = computedStyle["column-span"];
      nodeContext.columnSpan =
        !insideNonRootMultiColumn &&
        columnSpan &&
        !Css.isDefaultingValue(columnSpan)
          ? columnSpan
          : null;
      if (!nodeContext.inline) {
        const breakAfter = computedStyle["break-after"];
        if (
          breakAfter &&
          !Css.isDefaultingValue(breakAfter) &&
          !(insideNonRootMultiColumn && breakAfter === Css.ident.column)
        ) {
          nodeContext.breakAfter = breakAfter.toString();
          if (Break.forcedBreakValues[nodeContext.breakAfter]) {
            // delete computedStyle["break-after"];

            // Instead of deleting, set to "column" so that the browser can handle it.
            computedStyle["break-after"] = Css.ident.column;
          }
        }
        const breakBefore = computedStyle["break-before"];
        if (
          breakBefore &&
          !Css.isDefaultingValue(breakBefore) &&
          !(insideNonRootMultiColumn && breakBefore === Css.ident.column)
        ) {
          nodeContext.breakBefore = breakBefore.toString();
          if (Break.forcedBreakValues[nodeContext.breakBefore]) {
            if (this.isAtStartOfPage()) {
              delete computedStyle["break-before"];
            } else {
              // Set to "column" so that the browser can handle it.
              computedStyle["break-before"] = Css.ident.column;
            }
          }
          if (nodeContext.fragmentIndex !== 1) {
            nodeContext.breakBefore = null;
          }
        }
        // Named page type
        const pageVal: Css.Val = computedStyle["page"];
        const specifiedPageType =
          pageVal && !Css.isDefaultingValue(pageVal)
            ? pageVal.toString()
            : null;
        let pageType = specifiedPageType;
        if (
          !pageType &&
          !nodeContext.parent &&
          (nodeContext.shadowContext ||
            display === Css.ident.table_header_group ||
            display === Css.ident.table_footer_group)
        ) {
          // Keep currentPageType for shadowContext (Fix for issue #1233)
          pageType = this.styler.cascade.currentPageType;
        }
        if (!pageType || pageType.toLowerCase() === "auto") {
          pageType = nodeContext.pageType;
        } else {
          nodeContext.pageType = pageType;
        }
        const hasExplicitPageType =
          specifiedPageType != null &&
          specifiedPageType.toLowerCase() !== "auto";
        const effectivePageType = hasExplicitPageType
          ? nodeContext.fragmentIndex === 1
            ? this.getEffectiveStartPageType(element, pageType)
            : (this.styler.cascade.currentPageType ?? pageType)
          : pageType;
        if (
          this.styler.cascade.currentPageType !== effectivePageType &&
          // Fixed positioned or running elements should not affect page type
          // unless page type is explicitly set
          !(
            !hasExplicitPageType &&
            computedStyle["position"] === Css.ident.fixed
          )
        ) {
          if (
            nodeContext.fragmentIndex === 1 &&
            !Break.isSpreadBreakValue(nodeContext.breakBefore)
          ) {
            nodeContext.breakBefore = "page";
          }
          // Fix for issue #1309
          if (effectivePageType !== this.styler.cascade.previousPageType) {
            this.styler.cascade.previousPageType =
              this.styler.cascade.currentPageType;
          }
          this.styler.cascade.currentPageType = effectivePageType;
        }
      }
      nodeContext.captionSide =
        (computedStyle["caption-side"] &&
          computedStyle["caption-side"].toString()) ||
        "top";
      const borderCollapse = computedStyle["border-collapse"];
      if (!borderCollapse || borderCollapse === Css.getName("separate")) {
        const borderSpacing = computedStyle["border-spacing"];
        let inlineBorderSpacing: Css.Val;
        let blockBorderSpacing: Css.Val;
        if (borderSpacing) {
          if (borderSpacing instanceof Css.SpaceList) {
            inlineBorderSpacing = borderSpacing.values[0];
            blockBorderSpacing = borderSpacing.values[1];
          } else {
            inlineBorderSpacing = blockBorderSpacing = borderSpacing;
          }
          if (inlineBorderSpacing.isNumeric()) {
            nodeContext.inlineBorderSpacing = Css.toNumber(
              inlineBorderSpacing,
              this.context,
            );
          }
          if (blockBorderSpacing.isNumeric()) {
            nodeContext.blockBorderSpacing = Css.toNumber(
              blockBorderSpacing,
              this.context,
            );
          }
        }
      }
      const footnotePolicy =
        (computedStyle["footnote-policy"] as Css.Ident) ||
        semanticFootnoteStyle.footnotePolicy;
      if (footnotePolicy && !Css.isDefaultingValue(footnotePolicy)) {
        computedStyle["--viv-footnote-policy"] = footnotePolicy;
      }
      nodeContext.footnotePolicy =
        footnotePolicy && !Css.isDefaultingValue(footnotePolicy)
          ? footnotePolicy
          : null;
      const firstPseudo = computedStyle["x-first-pseudo"] as Css.Int;
      if (firstPseudo) {
        const outerPseudo = nodeContext.parent
          ? nodeContext.parent.firstPseudo
          : null;
        nodeContext.firstPseudo = new Vtree.FirstPseudo(
          outerPseudo,
          /** Css.Int */
          firstPseudo.num,
        );
      }
      if (!nodeContext.inline) {
        this.processAfterIfcontinues(
          nodeContext,
          element,
          elementStyle,
          styler,
          this.context,
        );
      }
      const whitespace = computedStyle["white-space"];
      if (whitespace) {
        const whitespaceValue = Vtree.whitespaceFromPropertyValue(
          whitespace.toString(),
        );
        if (whitespaceValue !== null) {
          nodeContext.whitespace = whitespaceValue;
        }
      }
      const hyphenateCharacter = computedStyle["hyphenate-character"];
      if (hyphenateCharacter && hyphenateCharacter instanceof Css.Str) {
        nodeContext.hyphenateCharacter = hyphenateCharacter.str;
      }
      const wordBreak = computedStyle["word-break"];
      const lineBreak = computedStyle["line-break"];
      const overflowWrap = computedStyle["overflow-wrap"];
      if (
        wordBreak === Css.ident.break_all ||
        lineBreak === Css.ident.anywhere ||
        overflowWrap === Css.ident.break_word ||
        overflowWrap === Css.ident.anywhere
      ) {
        nodeContext.breakWord = true;
      }

      // Resolve formatting context
      this.resolveFormattingContext(
        nodeContext,
        firstTime,
        display,
        position,
        floatSide,
        isRoot,
      );
      if (nodeContext.parent) {
        firstTime = nodeContext.parent.formattingContext.isFirstTime(
          nodeContext,
          firstTime,
        );
      }
      if (!nodeContext.inline) {
        nodeContext.repeatOnBreak = this.processRepeatOnBreak(computedStyle);
        this.findAndProcessRepeatingElements(nodeContext, element, styler);
      }

      // Create the view element
      let custom = false;
      let inner: Element | null = null;
      const fetchers: TaskUtil.Fetcher<string>[] = [];
      let ns = element.namespaceURI;
      let tag = element.localName;
      let originalTag = tag;
      if (ns == Base.NS.XHTML) {
        // WebKit recognizes these roles only on native HTML ruby elements.
        // Render CSS ruby spans with those native elements.
        if (Base.browserType === "webkit" && tag === "span") {
          if (display === Css.getName("ruby")) {
            tag = "ruby";
          } else if (display === Css.getName("ruby-base")) {
            tag = "rb";
          } else if (display === Css.getName("ruby-text")) {
            tag = "rt";
          }
        }
        if (xhtmlElementsRenderedAsDiv.has(tag)) {
          tag = "div";
        } else if (tag == "vide_") {
          tag = "video";
        } else if (tag == "audi_") {
          tag = "audio";
        } else if (tag == "object") {
          custom = true;
        }
        if (
          element.hasAttribute(PseudoElement.PSEUDO_ATTR) &&
          element.hasAttribute("src") &&
          computedStyle["content"] instanceof Css.URL
        ) {
          tag = "img";
        }
      } else if (ns == Base.NS.epub) {
        tag = "span";
        ns = Base.NS.XHTML;
      } else if (ns == Base.NS.SHADOW) {
        ns = Base.NS.XHTML;
        tag = nodeContext.inline ? "span" : "div";
      } else {
        custom = true;
      }
      if (isListItem) {
        // Keep display: list-item so the browser's native ::marker is used.
        if (firstTime) {
          tag = "li";
          if (computedStyle["--viv-marker-content"]) {
            // We control marker content via --viv-marker-content CSS custom
            // property. WebKit cannot render content on ::marker, so fall
            // back to list-style-* when the resolved marker can be expressed
            // there. (Issue #2017)
            if (!applyWebKitMarkerFallback(computedStyle)) {
              computedStyle["list-style-type"] = Css.ident.none;
              computedStyle["list-style-image"] = Css.ident.none;
            }
          }
        } else {
          // Subsequent fragments: no marker
          display = Display.isInlineLevel(display)
            ? Css.ident.inline
            : Css.ident.block;
          computedStyle["display"] = display;
          tag = "div";
        }
      } else if (tag == "body" || tag == "li") {
        tag = "div";
      } else if (tag == "q") {
        tag = "span";
      }
      if (computedStyle["behavior"]) {
        const behavior = computedStyle["behavior"].toString();
        if (behavior != "none") {
          custom = true;
        }
      }
      if (
        (element as HTMLElement).dataset &&
        element.getAttribute("data-math-typeset") === "true"
      ) {
        custom = true;
      }
      let elemResult: Task.Result<Element | null>;
      if (custom) {
        const parentNode = nodeContext.parent
          ? nodeContext.parent.viewNode
          : null;
        elemResult = this.customRenderer(
          element,
          parentNode as Element,
          computedStyle,
        );
      } else {
        elemResult = Task.newResult(null);
      }
      elemResult.then((result) => {
        if (result) {
          if (custom) {
            needToProcessChildren =
              result.getAttribute("data-adapt-process-children") == "true";
            // A custom-rendered subtree (e.g. MathML imported for MathJax)
            // keeps the source's style attributes instead of being styled
            // through Base.setCSSProperty(), which registers the documents
            // with annotation styles (see ruby-emphasis.ts).
            if (
              result.matches('[style*="text-emphasis"]') ||
              result.querySelector('[style*="text-emphasis"]')
            ) {
              Base.markAnnotatedDocument(result.ownerDocument);
            }
          }
        } else if (computedStyle["display"] === Css.ident.none) {
          // No element should be created if display:none is set.
          // (Fix issue #924)
          frame.finish(false);
          return;
        } else {
          result = this.createElement(ns, tag);
        }

        if (
          !isRoot &&
          isMultiColumn &&
          computedStyle["column-fill"] === Css.ident.auto
        ) {
          const blockSize = nodeContext.vertical
            ? computedStyle["width"]
            : computedStyle["height"];
          if (
            !blockSize ||
            blockSize === Css.ident.auto ||
            Css.isDefaultingValue(blockSize)
          ) {
            // To make `column-fill: auto` with auto block size work on non-root multi-column,
            // we change it to `balance` here, and after layout, we change it back.
            // (Issue #1720, #1758)
            result.setAttribute("data-viv-saved-column-fill", "auto");
            computedStyle["column-fill"] = Css.ident.balance;
          }
        }

        if (tag != originalTag) {
          result.setAttribute("data-vivliostyle-original-tag", originalTag);
          if (originalTag === "html" || originalTag === "body") {
            result.role = "presentation";
          }
        }
        if (tag == "a") {
          result.addEventListener("click", this.page.hrefHandler, false);
        }
        if (inner) {
          this.applyPseudoelementStyle(nodeContext, "inner", inner);
          result.appendChild(inner);
        }
        if (
          result.localName == "iframe" &&
          result.namespaceURI == Base.NS.XHTML
        ) {
          initIFrame(result as HTMLIFrameElement);
        }
        const imageResolution = nodeContext.inheritedProps[
          "image-resolution"
        ] as number | undefined;
        const images: {
          image: HTMLElement;
          element: HTMLElement;
          fetcher: TaskUtil.Fetcher<string>;
        }[] = [];
        const cssWidth = computedStyle["width"];
        const cssHeight = computedStyle["height"];
        const attrWidth = element.getAttribute("width");
        const attrHeight = element.getAttribute("height");
        const hasAutoWidth =
          cssWidth === Css.ident.auto || (!cssWidth && !attrWidth);
        const hasAutoHeight =
          cssHeight === Css.ident.auto || (!cssHeight && !attrHeight);
        const attributes = element.attributes;
        const attributeCount = attributes.length;
        let delayedSrc: string | null = null;
        let delayedAlt: string | null = null;

        // Workaround for issue #1439
        // `<tr><a id="…"></a><td>…` causes table layout issue, so change to
        // `<tr><td><a id="…"></a>…`
        const parentLastElementChild = (
          nodeContext.parent?.viewNode as Element | null
        )?.lastElementChild;
        if (
          display === Css.ident.table_cell &&
          parentLastElementChild?.localName === "a"
        ) {
          result.appendChild(parentLastElementChild);
        }

        for (let i = 0; i < attributeCount; i++) {
          const attribute = attributes[i];
          const attributeNS = attribute.namespaceURI;
          let attributeName = attribute.localName;
          let attributeValue = attribute.value;
          if (!attributeNS) {
            if (!Scripts.allowScripts && attributeName.startsWith("on")) {
              continue; // don't propagate JavaScript code
            }
            if (attributeName == "style") {
              continue; // we do styling ourselves
            }
            if (
              attributeName === "media" &&
              tag === "source" &&
              ns === Base.NS.XHTML &&
              element.parentElement?.localName === "picture"
            ) {
              // Evaluate the media query on <source> inside <picture>
              // using Vivliostyle's media context (print mode by default).
              // The browser evaluates media queries in screen mode,
              // so we need to adjust the media attribute to match.
              try {
                const scope = this.context.rootScope;
                const handler = new CssParser.ParserHandler(scope);
                const tokenizer = new CssTokenizer.Tokenizer(
                  attributeValue,
                  handler,
                );
                const condExpr = CssParser.parseMediaQuery(
                  tokenizer,
                  handler,
                  "",
                );
                if (condExpr) {
                  const matches = condExpr.toExpr().evaluate(this.context);
                  if (matches) {
                    // Media matches: remove the media attribute so the
                    // browser uses this source unconditionally.
                    continue;
                  } else {
                    // Media doesn't match: prevent browser from using
                    // this source.
                    attributeValue = "not all";
                  }
                }
              } catch {
                // If parsing fails, leave the attribute as-is.
              }
            }
            if (attributeName == "id" || attributeName == "name") {
              // Propagate transformed ids and collect them on the page
              // (only first time).
              if (firstTime) {
                const transformedValue =
                  this.documentURLTransformer.transformFragment(
                    attributeValue,
                    this.xmldoc.url,
                  );

                // Keep original id value so that JavaScript can manipulate it
                result.setAttribute(attributeName, attributeValue);
                result.setAttribute("data-vivliostyle-id", transformedValue);

                // Create an anchor element with transformed id, necessary for internal links in output PDF
                // (issue #877)
                const anchorElem = result.ownerDocument.createElement("a");
                anchorElem.setAttribute(attributeName, transformedValue);
                anchorElem.setAttribute(LayoutHelper.SPECIAL_ATTR, "1");
                anchorElem.style.position = "absolute";
                result.appendChild(anchorElem);

                // A fragment resolves to the first matching source element.
                // Retain every element for navigation and triggers, but do not
                // let later duplicates supply cross-reference snapshots.
                const target = this.xmldoc.getElement(`#${attributeValue}`);
                this.page.registerElementWithId(
                  result,
                  transformedValue,
                  !target || target === element,
                );
                continue;
              }
            }

            // TODO: understand the element we are working with.
            if (
              attributeName == "src" ||
              attributeName == "href" ||
              attributeName == "poster"
            ) {
              attributeValue = this.resolveURL(attributeValue);
              if (attributeName === "href") {
                attributeValue = this.documentURLTransformer.transformURL(
                  attributeValue,
                  this.xmldoc.url,
                );
              } else {
                // Convert WPT raw.githack.com URLs to wpt.live for resource
                // attributes (src, poster) so dynamic endpoints work.
                attributeValue = Base.resolveWptResourceURL(attributeValue);
              }
              // Convert about:blank to data:text/html, so iframes and other
              // embedded elements load natively without stalling on a missed
              // load event.
              if (attributeName === "src") {
                attributeValue = Base.convertAboutBlankURL(attributeValue);
              }
            } else if (attributeName == "srcset") {
              attributeValue = attributeValue
                .split(",")
                .map((entry) => {
                  const parts = entry.trim().split(/\s+/);
                  const url = Base.resolveWptResourceURL(
                    this.resolveURL(parts[0]),
                  );
                  return parts.length > 1
                    ? `${url} ${parts.slice(1).join(" ")}`
                    : url;
                })
                .join(",");
            } else if (
              attributeName === "data" &&
              custom &&
              tag === "object" &&
              result.hasAttribute("data")
            ) {
              // the data attribute is already set in OPFView.makeObjectView()
              continue;
            }
            if (
              attributeName === "poster" &&
              tag === "video" &&
              ns === Base.NS.XHTML
            ) {
              const image = new Image();
              const fetcher = Net.loadElement(image, attributeValue);
              if (hasAutoWidth && hasAutoHeight) {
                fetchers.push(fetcher);
                images.push({
                  image,
                  element: result as HTMLElement,
                  fetcher,
                });
              } else {
                this.page.fetchers.push(fetcher);
              }
            }
          } else if (attributeNS == "http://www.w3.org/2000/xmlns/") {
            continue; // namespace declaration (in Firefox)
          } else if (attributeNS == Base.NS.XLINK) {
            if (attributeName == "href") {
              attributeValue = this.documentURLTransformer.transformURL(
                this.resolveURL(attributeValue),
                this.xmldoc.url,
              );
            }
          }
          if (this.isSVGUrlAttribute(attributeName)) {
            attributeValue = Urls.transformURIs(
              attributeValue,
              this.xmldoc.url,
              this.documentURLTransformer,
            );
          }
          if (attributeNS) {
            const attributePrefix = namespacePrefixMap[attributeNS];
            if (attributePrefix) {
              attributeName = `${attributePrefix}:${attributeName}`;
            }
          }
          if (
            attributeName == "src" &&
            !attributeNS &&
            (tag == "img" || tag == "input") &&
            ns == Base.NS.XHTML
          ) {
            // HTML img element should start loading only once all
            // attributes are assigned.
            delayedSrc = attributeValue;
          } else if (
            attributeName == "alt" &&
            !attributeNS &&
            tag == "img" &&
            ns == Base.NS.XHTML
          ) {
            // The alt attribute should be assigned after the src attribute
            // to avoid the image box size being determined by the alt text.
            // (Issue #1343)
            delayedAlt = attributeValue;
          } else if (
            attributeName == "href" &&
            tag == "image" &&
            ns == Base.NS.SVG &&
            (attributeNS == Base.NS.XLINK || !attributeNS)
          ) {
            // For SVG2 namespace-less href, also set the href attribute
            // on the element since loadElement only sets xlink:href.
            if (!attributeNS) {
              result.setAttribute(attributeName, attributeValue);
            }
            this.page.fetchers.push(Net.loadElement(result, attributeValue));
          } else {
            // When the document is not XML document (e.g. non-XML HTML)
            // attributeNS can be null
            if (attributeNS) {
              result.setAttributeNS(attributeNS, attributeName, attributeValue);
            } else {
              try {
                result.setAttribute(attributeName, attributeValue);
              } catch (err) {
                Logging.logger.warn(err);
              }
            }
          }
        }
        if (delayedSrc) {
          const image = tag === "input" ? new Image() : result;
          const imageFetcher = Net.loadElement(image, delayedSrc, delayedAlt);
          if (image !== result) {
            (result as HTMLImageElement).src = delayedSrc;
          }
          const isInsideRunningElement = (): boolean => {
            // Check if the image is inside a position:fixed or running element
            // which is converted to position:fixed.
            if (computedStyle["position"] === Css.ident.fixed) {
              return true;
            }
            for (
              let p = nodeContext.parent?.viewNode as HTMLElement | null;
              p && p !== this.viewRoot;
              p = p.parentElement
            ) {
              if (p.style?.["position"] === "fixed") {
                return true;
              }
            }
            return false;
          };
          const hasPercentBlockSize = (): boolean => {
            // Check if the image has percentage block size, which is usually same as auto,
            // which means the size is not determined until the image is loaded.
            const blockSize = nodeContext.vertical ? cssWidth : cssHeight;
            return blockSize instanceof Css.Numeric && blockSize.unit === "%";
          };
          if (
            !hasAutoWidth &&
            !hasAutoHeight &&
            !hasPercentBlockSize() &&
            !isInsideRunningElement()
          ) {
            // No need to wait for the image, does not affect layout
            this.page.fetchers.push(imageFetcher);
          } else {
            if (
              hasAutoWidth &&
              hasAutoHeight &&
              imageResolution &&
              imageResolution !== 1
            ) {
              images.push({
                image: image as HTMLElement,
                element: result as HTMLElement,
                fetcher: imageFetcher,
              });
            }
            fetchers.push(imageFetcher);
          }
        } else if (
          !delayedSrc &&
          tag === "img" &&
          ns === Base.NS.XHTML &&
          ((result as HTMLImageElement).srcset ||
            element.parentElement?.localName === "picture")
        ) {
          // <img srcset="..."> without src, or <img> inside <picture>:
          // wait for the image to load.
          // Use page.fetchers (not layout-blocking fetchers) because the
          // image source may only be resolved after DOM insertion (e.g.,
          // <picture><source srcset="..."><img></picture>).
          this.page.fetchers.push(Net.loadElement(result));
        }
        delete computedStyle["content"];

        // Preload marker images referenced via --viv-marker-content so that
        // pagination waits for them, avoiding late reflow.
        const markerContent = computedStyle["--viv-marker-content"];
        if (markerContent) {
          const markerValues =
            markerContent instanceof Css.SpaceList
              ? markerContent.values
              : [markerContent];
          for (const v of markerValues) {
            if (v instanceof Css.URL && v.url) {
              fetchers.push(Net.loadElement(new Image(), v.url));
            }
          }
        }

        // Wait for object element content to load (e.g., embedded HTML via
        // <object data="...">). This ensures proper pagination by waiting for
        // the embedded document to fully render before proceeding.
        // Use result.localName instead of tag because custom renderer may
        // convert <object> to <iframe> (e.g., for bound content).
        if (
          result.localName === "object" &&
          result.namespaceURI === Base.NS.XHTML &&
          result.hasAttribute("data")
        ) {
          this.page.fetchers.push(Net.loadElement(result));
        }

        // Wait for embed element content to load (e.g., <embed src="image.png">).
        if (
          result.localName === "embed" &&
          result.namespaceURI === Base.NS.XHTML &&
          result.hasAttribute("src")
        ) {
          this.page.fetchers.push(Net.loadElement(result));
        }

        // Wait for iframe content to load. This ensures embedded documents
        // (including their images and resources) are fully rendered.
        // Handle loading="lazy" by forcing eager loading to prevent blocking.
        if (
          result.localName === "iframe" &&
          result.namespaceURI === Base.NS.XHTML &&
          result.hasAttribute("src")
        ) {
          const iframeElem = result as HTMLIFrameElement;
          if (iframeElem.loading === "lazy") {
            iframeElem.loading = "eager";
          }
          this.page.fetchers.push(Net.loadElement(result));
        }

        this.preprocessElementStyle(nodeContext, computedStyle);
        this.applyComputedStyles(result, computedStyle);
        RubyEmphasis.registerViewContext(result, nodeContext, position);
        if (computedStyle["ruby-position"]) {
          result.setAttribute(
            "data-viv-ruby-position",
            computedStyle["ruby-position"].toString(),
          );
        }
        RubyEmphasis.prepareRubyAnnotation(
          result,
          nodeContext.parent?.viewNode as HTMLElement | null,
          computedStyle,
          this.viewport.window,
          nodeContext.parent?.sourceNode as Element | null,
          (source) =>
            CssCascade.getProp(styler.getStyle(source, false), "display")
              ?.evaluate(this.context, "display")
              .toString(),
          cascadedMargins,
        );

        if (nodeContext.inline) {
          if (!firstTime) {
            Break.setBoxBreakFlag(result, "inline-start");
            if (Break.isCloneBoxDecorationBreak(result)) {
              Break.setBoxBreakFlag(result, "clone");
            }
          }
        } else {
          if (!firstTime) {
            const blockSizeP = nodeContext.vertical ? "width" : "height";
            if (Base.getCSSProperty(result, blockSizeP)) {
              // When a box with a specified block size is fragmented,
              // the fragmented box should not have the block size.
              // If the box is table-row, set a very small block size
              // (0 does not work in Firefox, so use 0.01px instead)
              // to prevent the row block size from expanding due to
              // large rowspanning cells.
              Base.setCSSProperty(
                result,
                blockSizeP,
                nodeContext.display === "table-row" ? "0.01px" : "",
              );
            }
            Break.setBoxBreakFlag(result, "block-start");
            if (Break.isCloneBoxDecorationBreak(result)) {
              Break.setBoxBreakFlag(result, "clone");
            }
            Break.setMarginDiscardFlag(result, "block-start");
          } else if (
            !Display.isAbsolutelyPositioned(computedStyle["position"])
          ) {
            const marginBreak = computedStyle["margin-break"];

            // Detect forced or unforced break at this point
            // to handle margin-break properly.
            const breakType = this.getBreakTypeAt(nodeContext);
            const anyBreak = breakType !== null;
            const unforcedBreak = breakType === "auto";
            if (
              (marginBreak === Css.ident.discard && anyBreak) ||
              (marginBreak !== Css.ident.keep &&
                unforcedBreak &&
                !nodeContext.floatSide)
            ) {
              Break.setMarginDiscardFlag(result, "block-start");
            }
          }
        }
        if (isListItem) {
          if (computedStyle["ua-list-item-count"]) {
            result.setAttribute(
              "value",
              computedStyle["ua-list-item-count"].stringValue(),
            );
          }
        }

        this.viewNode = result;
        if (fetchers.length) {
          TaskUtil.waitForFetchers(fetchers).then(() => {
            if (imageResolution !== undefined && imageResolution > 0) {
              this.modifyElemDimensionWithImageResolution(
                images,
                imageResolution,
                computedStyle,
                nodeContext.vertical,
              );
            }
            frame.finish(needToProcessChildren);
          });
        } else {
          frame.timeSlice().then(() => {
            frame.finish(needToProcessChildren);
          });
        }
      });
    });
    return frame.result();
  }

  /**
   * Check if the current position is at the start of a page.
   * This is used to determine whether a forced break should be ignored.
   * (Issue #1608)
   */
  private isAtStartOfPage(): boolean {
    for (let nc = this.nodeContext; nc?.parent && !nc.after; nc = nc.parent) {
      let node =
        nc === this.nodeContext && !nc.viewNode
          ? nc.parent.viewNode?.lastChild
          : nc.viewNode?.previousSibling;
      while (
        node &&
        (Vtree.canIgnore(node, nc.parent.whitespace) ||
          LayoutHelper.isOutOfFlow(node))
      ) {
        node = node.previousSibling;
      }
      if (node) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if the current element is inside multi-column element
   * that is not root or body element in the source tree.
   *
   * Note: vivliostyle handles multi-column on the root and body element on its own,
   * but leaves it to the browser to handle other multi-column.
   * This check is for such non-root/body multi-column.
   */
  private isInsideNonRootMultiColumn(nodeContext: Vtree.NodeContext): boolean {
    const element = nodeContext.parent?.viewNode as Element;
    return !!(element && LayoutHelper.findAncestorNonRootMultiColumn(element));
  }

  /**
   * Check if the current position is at a forced or unforced break
   * (Fix for Issue #690)
   *
   * @param nodeContext
   * @returns forced break type, or "auto" for unforced break, or null for not break
   */
  private getBreakTypeAt(nodeContext: Vtree.NodeContext): string | null {
    // Skip break detection for tables; fragmentation is handled by table.ts
    if (this.isInsideTable(nodeContext)) {
      return null;
    }
    for (
      let nc: Vtree.NodeContext | null = nodeContext;
      nc && !nc.after;
      nc = nc.parent
    ) {
      if (Break.isForcedBreakValue(nc.breakBefore)) {
        return nc.breakBefore; // forced break
      }
      const parent = nc.parent;
      if (nc.fragmentIndex === 1 && !parent) {
        if (nc.sourceNode === nc.sourceNode.ownerDocument?.documentElement) {
          // beginning of document
          return "page";
        } else {
          // page floats, etc.
          return null;
        }
      }
      if (!parent) {
        continue;
      }
      if (parent.floatSide) {
        // inside float, not break (Issue #1282)
        return null;
      }
      const parentViewNode = parent.viewNode as Element;
      if (parentViewNode) {
        // Check the content before the computed style, which forces a style
        // recalculation.
        let node = parentViewNode?.firstChild;
        while (
          node &&
          (Vtree.canIgnore(node, parent.whitespace) ||
            (!nc.floatSide && LayoutHelper.isOutOfFlow(node)))
        ) {
          node = node.nextSibling;
        }
        if (node && node !== nc.viewNode) {
          // parent's viewNode already has other content
          return null;
        }
        const style = this.viewport.window.getComputedStyle(parentViewNode);
        const paddingBlockStart = parseFloat(style.paddingBlockStart);
        const borderBlockStartWidth = parseFloat(style.borderBlockStartWidth);
        if (paddingBlockStart || borderBlockStartWidth) {
          // parent's viewNode has block-start padding or border
          return null;
        }
      }
    }

    const startBreakType =
      (
        this.context as Exprs.Context & {
          currentLayoutPosition: Vtree.LayoutPosition | null;
        }
      )?.currentLayoutPosition?.flowPositions[this.flowName]?.startBreakType ??
      null;

    if (Break.isForcedBreakValue(startBreakType)) {
      return startBreakType; // forced break
    } else {
      return "auto"; // unforced break
    }
  }

  private isInsideTable(nodeContext: Vtree.NodeContext): boolean {
    for (
      let nc: Vtree.NodeContext | null = nodeContext;
      nc && !nc.after;
      nc = nc.parent
    ) {
      if (nc.display && nc.display.startsWith("table")) {
        return true;
      }
    }
    return false;
  }

  private processAfterIfcontinues(
    nodeContext: Vtree.NodeContext,
    element: Element,
    cascStyle: CssCascade.ElementStyle,
    styler: CssStyler.AbstractStyler,
    context: Exprs.Context,
  ) {
    const pseudoMap = this.getPseudoMap(
      cascStyle,
      this.regionIds,
      this.isFootnote,
      nodeContext,
      context,
    );
    if (!pseudoMap) {
      return;
    }
    if (
      pseudoMap["after-if-continues"] &&
      pseudoMap["after-if-continues"]["content"]
    ) {
      const shadowStyler = new PseudoElement.PseudoelementStyler(
        element,
        cascStyle,
        styler,
        context,
        this.exprContentListener,
      );
      nodeContext.afterIfContinues = new Layout.AfterIfContinues(
        element,
        shadowStyler,
      );
    }
  }

  isSVGUrlAttribute(attributeName: string): boolean {
    return ViewFactory.SVG_URL_ATTRIBUTES.includes(attributeName.toLowerCase());
  }

  modifyElemDimensionWithImageResolution(
    images: {
      image: HTMLElement;
      element: HTMLElement;
      fetcher: TaskUtil.Fetcher<string>;
    }[],
    imageResolution: number,
    computedStyle: { [key: string]: Css.Val },
    isVertical: boolean,
  ) {
    images.forEach((param) => {
      if (param.fetcher.get().get() === "load") {
        const img = param.image;
        let scaledWidth = (img as HTMLImageElement).width / imageResolution;
        let scaledHeight = (img as HTMLImageElement).height / imageResolution;
        const elem = param.element;
        if (scaledWidth > 0 && scaledHeight > 0) {
          if (computedStyle["box-sizing"] === Css.ident.border_box) {
            if (computedStyle["border-left-style"] !== Css.ident.none) {
              scaledWidth += Css.toNumber(
                computedStyle["border-left-width"],
                this.context,
              );
            }
            if (computedStyle["border-right-style"] !== Css.ident.none) {
              scaledWidth += Css.toNumber(
                computedStyle["border-right-width"],
                this.context,
              );
            }
            if (computedStyle["border-top-style"] !== Css.ident.none) {
              scaledHeight += Css.toNumber(
                computedStyle["border-top-width"],
                this.context,
              );
            }
            if (computedStyle["border-bottom-style"] !== Css.ident.none) {
              scaledHeight += Css.toNumber(
                computedStyle["border-bottom-width"],
                this.context,
              );
            }
          }
          if (imageResolution > 1) {
            const maxWidth = computedStyle["max-width"] || Css.ident.none;
            const maxHeight = computedStyle["max-height"] || Css.ident.none;
            if (maxWidth === Css.ident.none && maxHeight === Css.ident.none) {
              Base.setCSSProperty(elem, "max-width", `${scaledWidth}px`);
            } else if (
              maxWidth !== Css.ident.none &&
              maxHeight === Css.ident.none
            ) {
              Base.setCSSProperty(elem, "width", `${scaledWidth}px`);
            } else if (
              maxWidth === Css.ident.none &&
              maxHeight !== Css.ident.none
            ) {
              Base.setCSSProperty(elem, "height", `${scaledHeight}px`);
            } else {
              // maxWidth != none && maxHeight != none
              Asserts.assert(maxWidth.isNumeric());
              Asserts.assert(maxHeight.isNumeric());
              const numericMaxWidth = maxWidth as Css.Numeric;
              const numericMaxHeight = maxHeight as Css.Numeric;
              if (numericMaxWidth.unit !== "%") {
                Base.setCSSProperty(
                  elem,
                  "max-width",
                  `${Math.min(
                    scaledWidth,
                    Css.toNumber(numericMaxWidth, this.context),
                  )}px`,
                );
              } else if (numericMaxHeight.unit !== "%") {
                Base.setCSSProperty(
                  elem,
                  "max-height",
                  `${Math.min(
                    scaledHeight,
                    Css.toNumber(numericMaxHeight, this.context),
                  )}px`,
                );
              } else {
                if (isVertical) {
                  Base.setCSSProperty(elem, "height", `${scaledHeight}px`);
                } else {
                  Base.setCSSProperty(elem, "width", `${scaledWidth}px`);
                }
              }
            }
          } else if (imageResolution < 1) {
            const minWidth = computedStyle["min-width"] || Css.numericZero;
            const minHeight = computedStyle["min-height"] || Css.numericZero;
            Asserts.assert(minWidth.isNumeric());
            const numericMinWidth = minWidth as Css.Numeric;
            const numericMinHeight = minHeight as Css.Numeric;
            if (numericMinWidth.num === 0 && numericMinHeight.num === 0) {
              Base.setCSSProperty(elem, "min-width", `${scaledWidth}px`);
            } else if (
              numericMinWidth.num !== 0 &&
              numericMinHeight.num === 0
            ) {
              Base.setCSSProperty(elem, "width", `${scaledWidth}px`);
            } else if (
              numericMinWidth.num === 0 &&
              numericMinHeight.num !== 0
            ) {
              Base.setCSSProperty(elem, "height", `${scaledHeight}px`);
            } else {
              // minWidth != 0 && minHeight != 0
              if (numericMinWidth.unit !== "%") {
                Base.setCSSProperty(
                  elem,
                  "min-width",
                  `${Math.max(
                    scaledWidth,
                    Css.toNumber(numericMinWidth, this.context),
                  )}px`,
                );
              } else if (numericMinHeight.unit !== "%") {
                Base.setCSSProperty(
                  elem,
                  "min-height",
                  `${Math.max(
                    scaledHeight,
                    Css.toNumber(numericMinHeight, this.context),
                  )}px`,
                );
              } else {
                if (isVertical) {
                  Base.setCSSProperty(elem, "height", `${scaledHeight}px`);
                } else {
                  Base.setCSSProperty(elem, "width", `${scaledWidth}px`);
                }
              }
            }
          }
        }
      }
    });
  }

  private preprocessElementStyle(
    nodeContext: Vtree.NodeContext,
    computedStyle: { [key: string]: Css.Val },
  ) {
    const hooks: Plugin.PreProcessElementStyleHook[] = Plugin.getHooksForName(
      Plugin.HOOKS.PREPROCESS_ELEMENT_STYLE,
    );
    hooks.forEach((hook) => {
      hook(nodeContext, computedStyle);
    });
  }

  private findAndProcessRepeatingElements(
    nodeContext: Vtree.NodeContext,
    element: Element,
    styler: CssStyler.AbstractStyler,
  ) {
    for (
      let child: Node | null = element.firstChild;
      child;
      child = child.nextSibling
    ) {
      if (child.nodeType !== 1) {
        continue;
      }
      const computedStyle: { [key: string]: Css.Val } = {};
      const elementStyle = styler.getStyle(child as Element, false);
      this.computeStyle(
        nodeContext.writingMode,
        nodeContext.direction === "rtl",
        elementStyle,
        computedStyle,
      );
      const processRepeatOnBreak = this.processRepeatOnBreak(computedStyle);
      if (!processRepeatOnBreak) {
        continue;
      }
      if (
        nodeContext.formattingContext instanceof
          RepetitiveElement.RepetitiveElementsOwnerFormattingContext &&
        !nodeContext.belongsTo(nodeContext.formattingContext)
      ) {
        return;
      }
      const parent = nodeContext.parent;
      const parentFormattingContext = parent ? parent.formattingContext : null;
      nodeContext.formattingContext =
        new RepetitiveElement.RepetitiveElementsOwnerFormattingContext(
          parentFormattingContext,
          nodeContext.sourceNode as Element,
        );
      (
        nodeContext.formattingContext as RepetitiveElement.RepetitiveElementsOwnerFormattingContext
      ).initializeRepetitiveElements(nodeContext.writingMode);
      return;
    }
  }

  private processRepeatOnBreak(computedStyle: { [key: string]: Css.Val }) {
    let repeatOnBreak = computedStyle["repeat-on-break"];
    if (repeatOnBreak !== Css.ident.none) {
      if (
        repeatOnBreak === Css.ident.auto ||
        Css.isDefaultingValue(repeatOnBreak)
      ) {
        if (computedStyle["display"] === Css.ident.table_header_group) {
          repeatOnBreak = Css.ident.header;
        } else if (computedStyle["display"] === Css.ident.table_footer_group) {
          repeatOnBreak = Css.ident.footer;
        } else {
          repeatOnBreak = Css.ident.none;
        }
      }
      if (repeatOnBreak && repeatOnBreak !== Css.ident.none) {
        return repeatOnBreak.toString();
      }
    }
    return null;
  }

  private createTextNodeView(
    nodeContext: Vtree.NodeContext,
  ): Task.Result<boolean> {
    const frame: Task.Frame<boolean> = Task.newFrame("createTextNodeView");
    this.preprocessTextContent(nodeContext).then((preprocessedTextContent) => {
      const offsetInNode = this.offsetInNode || 0;
      const textContent = Diff.restoreNewText(preprocessedTextContent).slice(
        offsetInNode,
      );
      this.viewNode = document.createTextNode(textContent);
      frame.finish(true);
    });
    return frame.result();
  }

  private preprocessTextContent(
    nodeContext: Vtree.NodeContext,
  ): Task.Result<Diff.Change[]> {
    if (nodeContext.preprocessedTextContent != null) {
      return Task.newResult(nodeContext.preprocessedTextContent);
    }
    let originl: string;
    let textContent = (originl = nodeContext.sourceNode.textContent ?? "");
    const frame: Task.Frame<Diff.Change[]> = Task.newFrame(
      "preprocessTextContent",
    );
    const hooks: Plugin.PreProcessTextContentHook[] = Plugin.getHooksForName(
      Plugin.HOOKS.PREPROCESS_TEXT_CONTENT,
    );
    let index = 0;
    frame
      .loop(() => {
        if (index >= hooks.length) {
          return Task.newResult(false);
        }
        return hooks[index++](nodeContext, textContent).thenAsync(
          (processedText) => {
            textContent = processedText;
            return Task.newResult(true);
          },
        );
      })
      .then(() => {
        const preprocessedTextContent = Diff.diffChars(originl, textContent);
        nodeContext.preprocessedTextContent = preprocessedTextContent;
        frame.finish(preprocessedTextContent);
      });
    return frame.result();
  }

  /**
   * @return holding true if children should be processed
   */
  createNodeView(
    nodeContext: Vtree.NodeContext,
    firstTime: boolean,
  ): Task.Result<boolean> {
    const frame: Task.Frame<boolean> = Task.newFrame("createNodeView");
    let result: Task.Result<boolean>;
    let needToProcessChildren = true;
    if (nodeContext.sourceNode.nodeType == 1) {
      result = this.createElementView(nodeContext, firstTime);
    } else {
      if (nodeContext.sourceNode.nodeType == 8) {
        this.viewNode = null; // comment node
        result = Task.newResult(true);
      } else {
        result = this.createTextNodeView(nodeContext);
      }
    }
    result.then((processChildren) => {
      needToProcessChildren = processChildren;
      nodeContext.viewNode = this.viewNode;
      if (this.viewNode) {
        const isPseudo = (node: Node | null, name: string): boolean =>
          node?.nodeType === 1 &&
          PseudoElement.getPseudoName(node as Element) === name;
        const p = nodeContext.parent;
        // A first-letter pseudo-element view is only generated below its
        // originating element's context, so a context bearing one has a parent.
        const pFirstLetter =
          p &&
          isPseudo(this.viewNode, "after") &&
          isPseudo(p.viewNode, "first-letter")
            ? (p as Vtree.ChildNodeContext)
            : null;
        let parent = p
          ? pFirstLetter?.viewNode?.hasChildNodes()
            ? (pFirstLetter.parent.viewNode as Element) // Fix for issue #1175
            : (p.viewNode as Element)
          : this.viewRoot;
        if (nodeContext.pluginProps["nestedFootnoteDetached"]) {
          // Nested footnote, attach to the root (Issue #1352)
          parent = this.viewRoot;
        }
        if (parent) {
          if (
            nodeContext.inline &&
            // ignore whitespace text node
            !(this.viewNode.nodeType === 3 && Vtree.canIgnore(this.viewNode)) &&
            !parent.hasChildNodes() &&
            Break.getBoxBreakFlags(parent).includes("block-start")
          ) {
            Break.setBoxBreakFlag(parent, "text-start");
          }
          parent.appendChild(this.viewNode);
          if (isPseudo(this.viewNode, "first-line")) {
            this.applyFirstLineMetricsToBlock(
              this.viewNode as HTMLElement,
              parent as HTMLElement,
            );
          }

          // Fix overflow caused by forced column breaks
          LayoutHelper.fixOverflowAtForcedColumnBreak(this.viewNode);
        }
      }
      frame.finish(needToProcessChildren);
    });
    return frame.result();
  }

  /** @override */
  setCurrent(
    nodeContext: Vtree.NodeContext,
    firstTime: boolean,
    atUnforcedBreak?: boolean,
  ): Task.Result<boolean> {
    this.nodeContext = nodeContext;
    this.sourceNode = nodeContext.sourceNode;
    this.offsetInNode = nodeContext.offsetInNode;
    this.viewNode = null;
    return this.createNodeView(nodeContext, firstTime);
  }

  processShadowContent(pos: Vtree.ChildNodeContext): Vtree.NodeContext {
    if (
      pos.shadowContext == null ||
      (pos.sourceNode as Element).localName != "content" ||
      (pos.sourceNode as Element).namespaceURI != Base.NS.SHADOW
    ) {
      return pos;
    }
    const boxOffset = pos.boxOffset;
    const shadow = pos.shadowContext;

    // content that will be inserted
    let contentNode: Node | null;
    let contentShadowType: Vtree.ShadowType;
    const contentShadow = shadow.subShadow || shadow.parentShadow;
    if (shadow.subShadow) {
      // If there is a nested shadow, insert that nested shadow's content
      // at this <shadow:content> slot.
      contentNode = shadow.subShadow.root;
      contentShadowType = shadow.subShadow.type;
      if (contentShadowType == Vtree.ShadowType.ROOTLESS) {
        contentNode = contentNode.firstChild;
      }
    } else {
      contentNode = shadow.owner.firstChild;
      contentShadowType = Vtree.ShadowType.ROOTLESS;
    }
    const nextSibling = pos.sourceNode.nextSibling;
    let nextPos: Vtree.NodeContext | null;
    if (nextSibling) {
      pos.sourceNode = nextSibling;
      pos.resetView();
      nextPos = pos;
    } else if (pos.shadowSibling) {
      nextPos = pos.shadowSibling;
    } else {
      nextPos = null;
    }
    if (contentNode) {
      const r = Vtree.NodeContext.childOf(contentNode, pos.parent, boxOffset);
      r.shadowContext = contentShadow;
      r.shadowType = contentShadowType;
      r.shadowSibling = nextPos;
      return this.processShadowContent(r);
    }
    if (nextPos === null) {
      nextPos = pos.parent.modify();
      nextPos.after = true;
    }
    nextPos.boxOffset = boxOffset;
    return nextPos;
  }

  private nextPositionInTree(
    pos: Vtree.NodeContext,
    preprocessedTextContent: Diff.Change[] | null,
  ): Vtree.NodeContext | null {
    let boxOffset = pos.boxOffset + 1; // offset for the next position
    if (pos.after) {
      // root, that was the last possible position
      let cur = Vtree.asChildNodeContext(pos);
      if (!cur) {
        return null;
      }

      // we are done with this sourceNode, see if there is a next sibling,
      // unless this is the root of the shadow tree
      if (cur.shadowType != Vtree.ShadowType.ROOTED) {
        const next = cur.sourceNode.nextSibling;
        if (next) {
          cur = cur.modify();

          // keep shadowType
          cur.boxOffset = boxOffset;
          cur.sourceNode = next;
          cur.resetView();
          return this.processShadowContent(cur);
        }
      }

      // if no viable siblings, check if there are shadow siblings
      if (cur.shadowSibling) {
        // our next position is the element after shadow:content in the parent
        // shadow tree
        const sibling = cur.shadowSibling.modify();
        sibling.boxOffset = boxOffset;
        return sibling;
      }

      // if not rootless shadow, move to the "after" position for the parent
      const afterParent = cur.parent.modify();
      afterParent.boxOffset = boxOffset;
      afterParent.after = true;
      return afterParent;
    } else {
      // any shadow trees?
      if (pos.nodeShadow) {
        let shadowNode: Node | null = pos.nodeShadow.root;
        if (pos.nodeShadow.type == Vtree.ShadowType.ROOTLESS) {
          shadowNode = shadowNode.firstChild;
        }
        if (shadowNode) {
          const sr = Vtree.NodeContext.childOf(shadowNode, pos, boxOffset);
          sr.shadowContext = pos.nodeShadow;
          sr.shadowType = pos.nodeShadow.type;
          return this.processShadowContent(sr);
        }
      }

      // any children?
      const child = pos.sourceNode.firstChild;
      if (child) {
        return this.processShadowContent(
          Vtree.NodeContext.childOf(child, pos, boxOffset),
        );
      }

      // no children - was there text content?
      if (preprocessedTextContent) {
        const content = Diff.restoreNewText(preprocessedTextContent);
        boxOffset += content.length - 1 - pos.offsetInNode;
      }
      pos = pos.modify();
      pos.boxOffset = boxOffset;
      pos.after = true;
      return pos;
    }
  }

  /**
   * Find a footnote-call element immediately before the given view node.
   */
  private findImmediateFootnoteCallSibling(
    footnoteNodeContext: Vtree.NodeContext,
  ): Element | null {
    const ownerOffset = this.xmldoc.getElementOffset(
      footnoteNodeContext.sourceNode as Element,
    );
    const viewNode = footnoteNodeContext.viewNode;
    if (!viewNode) {
      return null;
    }
    let prev: Node | null = viewNode.previousSibling;
    while (prev) {
      if (prev.nodeType === Node.TEXT_NODE) {
        if ((prev.textContent ?? "").trim().length === 0) {
          prev = prev.previousSibling;
          continue;
        }
        return null;
      }
      if (prev.nodeType === Node.ELEMENT_NODE) {
        const prevElem = prev as Element;
        return PseudoElement.getPseudoName(prevElem) === "footnote-call" &&
          prevElem.getAttribute(ViewFactory.FOOTNOTE_CALL_OWNER_ATTR) ===
            ownerOffset.toString()
          ? prevElem
          : null;
      }
      return null;
    }
    return null;
  }

  /**
   * True for semantic footnotes that already provide a noteref call in source
   * (EPUB or DPUB-ARIA pattern), so an extra ::footnote-call must not be inserted.
   */
  private isSemanticFootnoteElement(footnoteNodeContext: Vtree.NodeContext) {
    const sourceNode = footnoteNodeContext.sourceNode;
    return (
      sourceNode instanceof Element &&
      SemanticFootnote.isSemanticFootnoteElement(sourceNode)
    );
  }

  /**
   * Insert a footnote-call NodeContext before the footnote element.
   * The footnote-call will be processed as a normal inline element,
   * then shadowSibling will lead to the footnote element.
   * (Issue #868)
   */
  private insertFootnoteCall(
    footnoteNodeContext: Vtree.NodeContext,
  ): Task.Result<Vtree.NodeContext> {
    const frame: Task.Frame<Vtree.NodeContext> =
      Task.newFrame("insertFootnoteCall");

    // Create a pseudo source node for footnote-call
    const footnoteCallSourceNode = PseudoElement.document.createElementNS(
      Base.NS.XHTML,
      "span",
    );
    PseudoElement.setPseudoName(footnoteCallSourceNode, "footnote-call");
    const footnoteOffset = this.xmldoc.getElementOffset(
      footnoteNodeContext.sourceNode as Element,
    );
    footnoteCallSourceNode.setAttribute(
      ViewFactory.FOOTNOTE_CALL_OWNER_ATTR,
      footnoteOffset.toString(),
    );

    // Create NodeContext for footnote-call with the same parent as footnote
    const footnoteCallContext = Vtree.NodeContext.siblingOf(
      footnoteCallSourceNode,
      footnoteNodeContext,
      footnoteNodeContext.boxOffset,
    );

    // Set up properties for inline element
    footnoteCallContext.inline = true;

    // Create shadow context for footnote-call styling
    // Use the footnote element's styler to get ::footnote-call styles
    const shadowStyler = new PseudoElement.PseudoelementStyler(
      footnoteNodeContext.sourceNode as Element,
      this.styler.getStyle(footnoteNodeContext.sourceNode as Element, false),
      this.styler,
      this.context,
      this.exprContentListener,
    );
    footnoteCallContext.shadowContext = new Vtree.ShadowContext(
      footnoteNodeContext.sourceNode as Element,
      footnoteCallSourceNode, // Use the span directly as root, not a shadow:root
      null,
      footnoteNodeContext.parent?.shadowContext || null,
      null,
      Vtree.ShadowType.ROOTLESS,
      shadowStyler,
    );

    // Set up shadow sibling to return to footnote after footnote-call is done
    footnoteCallContext.shadowSibling =
      footnoteNodeContext as Vtree.NodeContext;

    // Increment footnote's boxOffset since footnote-call takes one position
    (footnoteNodeContext as Vtree.NodeContext).boxOffset++;

    // Remove the footnote element's viewNode from DOM if it was already created
    // (setCurrent was called before we detected this is a footnote)
    if (footnoteNodeContext.viewNode?.parentNode) {
      footnoteNodeContext.viewNode.parentNode.removeChild(
        footnoteNodeContext.viewNode,
      );
    }

    // Now create the view for footnote-call
    this.setCurrent(footnoteCallContext, true, false).then(
      (processChildren) => {
        // If processChildren is true and the source node has children (generated content),
        // let the normal layout flow process them by NOT setting after=true.
        // This ensures text-spacing polyfill is applied via postLayoutBlock.
        if (processChildren && footnoteCallSourceNode.hasChildNodes()) {
          // Return the footnote-call context as-is to process children
          frame.finish(footnoteCallContext);
        } else {
          // No children to process, mark as done
          const modified = footnoteCallContext.modify();
          modified.after = true;
          if (!footnoteCallContext.viewNode) {
            modified.inline = true;
          }
          frame.finish(modified);
        }
      },
    );

    return frame.result();
  }

  /** @override */
  nextInTree(
    position: Vtree.NodeContext,
    atUnforcedBreak?: boolean,
  ): Task.Result<Vtree.NodeContext | null> {
    const preprocessResult: Task.Result<Diff.Change[] | null> =
      !position.after && position.sourceNode.nodeType != 1
        ? this.preprocessTextContent(position)
        : Task.newResult<Diff.Change[] | null>(null);
    return preprocessResult.thenAsync((preprocessedTextContent) =>
      this.nextInTreeWithPreprocessedText(
        position,
        preprocessedTextContent,
        atUnforcedBreak,
      ),
    );
  }

  private nextInTreeWithPreprocessedText(
    position: Vtree.NodeContext,
    preprocessedTextContent: Diff.Change[] | null,
    atUnforcedBreak?: boolean,
  ): Task.Result<Vtree.NodeContext | null> {
    const nextPosition = this.nextPositionInTree(
      position,
      preprocessedTextContent,
    );
    if (!nextPosition || nextPosition.after) {
      return Task.newResult<Vtree.NodeContext | null>(nextPosition);
    }
    this.syncTextNodePageTypeBoundary(nextPosition);
    let nodeContext = nextPosition;
    const frame: Task.Frame<Vtree.NodeContext | null> =
      Task.newFrame("nextInTree");
    this.setCurrent(nodeContext, true, atUnforcedBreak).then(
      (processChildren) => {
        if (!nodeContext.viewNode || !processChildren) {
          nodeContext = nodeContext.modify();
          nodeContext.after = true; // skip
          if (!nodeContext.viewNode) {
            nodeContext.inline = true;
          }
        }

        // Issue #868: Insert footnote-call before footnote element
        // Only insert if footnote-call hasn't been processed yet for this footnote.
        // Skip semantic EPUB/DPUB footnotes because the call already exists in
        // source HTML as a noteref link (no extra ::footnote-call is needed).
        // Also skip pseudo-elements (shadowContext exists) because they are not in the
        // source document tree (PR #1607 follow-up fix).
        if (
          nodeContext.floatSide === "footnote" &&
          !(this.isFootnote && !nodeContext.parent) &&
          !nodeContext.after &&
          !nodeContext.pluginProps["footnoteCallProcessed"] &&
          !nodeContext.shadowContext &&
          !this.isSemanticFootnoteElement(nodeContext)
        ) {
          const existingFootnoteCall =
            this.findImmediateFootnoteCallSibling(nodeContext);
          if (existingFootnoteCall?.parentNode) {
            existingFootnoteCall.parentNode.removeChild(existingFootnoteCall);
          }
          if (this.isFootnote && nodeContext.parent) {
            // Detach nested footnote content into footnote area while keeping
            // the call marker in the parent footnote text. (Issue #1352)
            nodeContext.pluginProps["nestedFootnoteDetached"] = 1;
          }
          // Mark as processed to avoid infinite loop when returning via shadowSibling
          nodeContext.pluginProps["footnoteCallProcessed"] = 1;
          // Return footnote-call first, footnote will be processed via shadowSibling
          this.insertFootnoteCall(nodeContext).then((footnoteCallContext) => {
            this.dispatchEvent({
              type: "nextInTree",
              nodeContext: footnoteCallContext,
            } as any);
            frame.finish(footnoteCallContext);
          });
          return;
        }

        this.dispatchEvent({ type: "nextInTree", nodeContext } as any);
        frame.finish(nodeContext);
      },
    );
    return frame.result();
  }

  addImageFetchers(bg: Css.Val) {
    addImageFetchersToPage(bg, this.page);
  }

  /**
   * The ::first-line span only affects its own inline box, while the strut of
   * the first line box (and a forced break ending the line) still uses the
   * block's font. Copy the span's metrics to the block so that the UA
   * stylesheet applies them to the block's real ::first-line, and the browser
   * lays out the first line box as for native ::first-line.
   */
  private applyFirstLineMetricsToBlock(
    span: HTMLElement,
    block: HTMLElement,
  ): void {
    if (!span.style || !block.style) {
      return;
    }
    const window = span.ownerDocument.defaultView;
    if (!window) {
      return;
    }
    const spanStyle = window.getComputedStyle(span);
    const blockStyle = window.getComputedStyle(block);
    // font-family and font-size inherit as computed values, so the resolved
    // values can be forwarded.
    for (const [propName, customPropName] of [
      ["font-family", "--viv-firstLineFontFamily"],
      ["font-size", "--viv-firstLineFontSize"],
    ]) {
      const value = spanStyle.getPropertyValue(propName);
      if (value && value !== blockStyle.getPropertyValue(propName)) {
        block.style.setProperty(customPropName, value);
        if (propName === "font-size") {
          // The span inherits from the real ::first-line, so a relative
          // font-size (e.g. `200%`) on the span must be resolved now, or it
          // would be applied twice.
          span.style.fontSize = value;
        }
      }
    }
    // A unitless line-height inherits as a number, so forward the span's
    // specified value only. A resolved length would be inherited by inline
    // children with other font sizes instead of the number.
    const lineHeight = span.style.getPropertyValue("line-height");
    if (lineHeight) {
      block.style.setProperty("--viv-firstLineLineHeight", lineHeight);
    }
  }

  applyComputedStyles(
    target: Element,
    computedStyle: { [key: string]: Css.Val },
  ) {
    const bg = computedStyle["background-image"];
    if (bg) {
      this.addImageFetchers(bg);
    }
    const borderImageSource = computedStyle["border-image-source"];
    if (borderImageSource) {
      this.addImageFetchers(borderImageSource);
    }
    const listStyleImage = computedStyle["list-style-image"];
    if (listStyleImage) {
      this.addImageFetchers(listStyleImage);
    }
    const maskImage = computedStyle["mask-image"];
    if (maskImage) {
      this.addImageFetchers(maskImage);
    }
    const clipPath = computedStyle["clip-path"];
    if (clipPath) {
      this.addImageFetchers(clipPath);
    }
    const filter = computedStyle["filter"];
    if (filter) {
      this.addImageFetchers(filter);
    }
    const shapeOutside = computedStyle["shape-outside"];
    if (shapeOutside) {
      this.addImageFetchers(shapeOutside);
    }
    const markerContent = computedStyle["--viv-marker-content"];
    if (markerContent) {
      this.addImageFetchers(markerContent);
    }
    const isRelativePositioned =
      computedStyle["position"] === Css.ident.relative;
    const isRoot =
      this.nodeContext?.parent === null &&
      this.sourceNode?.parentElement === null &&
      !!this.viewRoot?.parentElement;

    const propList = Object.keys(computedStyle);
    propList.sort(Css.processingOrderFn);
    let fontSize: Css.Val | undefined;
    let lineHeight: Css.Val | undefined;
    // Whether this element's own font size carries the adjustment by
    // layoutUnitAdj, or null when it takes that of the parent along.
    let ownFontSizeAdjusted: boolean | null = null;

    for (const propName of propList) {
      if (propertiesNotPassedToDOM[propName]) {
        continue;
      }
      let value = computedStyle[propName];
      if (!value || (value === Css.empty && !Css.isCustomPropName(propName))) {
        continue;
      }
      value = value.visit(
        new CssProp.UrlTransformVisitor(
          this.xmldoc.url,
          this.documentURLTransformer,
        ),
      );
      if (value instanceof Css.Numeric) {
        if (value.unit === "em") {
          const emUnitSize = this.getEmUnitSize(propName, fontSize);
          if (emUnitSize != null) {
            value = new Css.Numeric(value.num * emUnitSize, "px");
          }
        } else if (value.unit === "lh") {
          const lhUnitSize = this.getLineHeightUnitSize(
            propName,
            fontSize,
            lineHeight,
          );
          if (lhUnitSize != null) {
            value = new Css.Numeric(value.num * lhUnitSize, "px");
          }
        } else if (Exprs.needUnitConversion(value.unit)) {
          // font-size for the root element is already converted to px
          value = Css.convertNumericToPx(value, this.context);
        }
      }

      const isFontSizeOrLineHeight =
        propName === "font-size" || propName === "line-height";
      if (isFontSizeOrLineHeight) {
        if (propName === "font-size") {
          fontSize = value;
        } else {
          lineHeight = value;
        }
      }

      // Adjust font-size and line-height to prevent rounding issues
      // when calculating the size of text and line boxes. (Issue #1590)
      //
      // Chromium and Firefox round to the nearest multiple of LayoutUnit
      // (the smallest unit of length in the browser) when positioning boxes,
      // which may cause the box size to be slightly larger than specified,
      // resulting in text not fitting in the specified number of characters/lines.
      // To prevent this, we adjust the font size and line height to be
      // 1 LayoutUnit less.
      const adjustsValue =
        isFontSizeOrLineHeight &&
        !!this.viewport.layoutUnitAdj &&
        value instanceof Css.Numeric &&
        value.unit !== "%" &&
        value.unit !== "em" &&
        value.unit !== "lh";

      if (propName === "font-size" && this.viewport.layoutUnitAdj) {
        ownFontSizeAdjusted = adjustsValue
          ? true
          : fontSizeCarriesAdjustment(value.toString());
        if (
          ownFontSizeAdjusted == null &&
          value instanceof Css.Func &&
          this.resolvesWithoutParentAdjustment(value)
        ) {
          ownFontSizeAdjusted = false;
          fontSizesWithoutParentAdjustment.add(target);
        } else {
          fontSizesWithoutParentAdjustment.delete(target);
        }
      }

      // Font-size and line-height values that are not adjusted, e.g. calc(),
      // stay smaller by resolving against the adjusted font. Compensating them
      // would undo the adjustment.
      if (
        this.viewport.layoutUnitAdj &&
        !Css.isCustomPropName(propName) &&
        (adjustsValue || !isFontSizeOrLineHeight) &&
        // Letter-spacing and word-spacing are added to the advances of the
        // text, which shrink with the adjusted font, and stay in proportion
        // to them, e.g. `letter-spacing: 1ch` to the advance of a space in a
        // monospace font, which tab-size is measured in. Shrinking them takes
        // no room away from the text.
        propName !== "letter-spacing" &&
        propName !== "word-spacing" &&
        hasCurrentFontRelativeLength(value) &&
        // The font that the units resolve against (the parent's one for
        // font-size) may not be adjusted, e.g. that of a parent with
        // `font-size: clamp(16px, 16px, 16px)`.
        ((propName === "font-size" ? null : ownFontSizeAdjusted) ??
          this.isInheritedFontSizeAdjusted())
      ) {
        value = this.compensateFontSizeAdjustment(
          value,
          propName,
          fontSize,
          computedStyle,
          target,
        );
      }

      if (adjustsValue) {
        value = new Css.AnyToken(
          `calc(${value.toString()} - var(--viv-layoutUnitAdj))`,
        );
      }

      if (
        Vtree.delayedProps[propName] ||
        (isRelativePositioned &&
          Vtree.delayedPropsIfRelativePositioned[propName])
      ) {
        // Set it after page layout is done.
        this.page.delayedItems.push(
          new Vtree.DelayedItem(target, propName, value),
        );
        continue;
      }
      const inheritedPropsMovedToPageBox =
        isRoot &&
        !!this.page.pageAreaElement &&
        CssCascade.isInherited(propName);
      if (inheritedPropsMovedToPageBox) {
        // Fix for Issue #568
        Base.setCSSProperty(
          // the page area element is always nested in bleed box and container
          this.page.pageAreaElement.parentElement!.parentElement!,
          propName,
          value.toString(),
        );
        // Also propagate to the layout box so that page float areas laid out
        // there (e.g. during stash re-layout before the root element is
        // processed on the new page) inherit the correct values. (Issue #1752)
        // Exclude writing-mode and direction: these affect page box positioning
        // inside the layout box (e.g. vertical-rl right-aligns children) and
        // are already set on the page box when needed.
        if (propName !== "writing-mode" && propName !== "direction") {
          Base.setCSSProperty(
            this.viewport.layoutBox,
            propName,
            value.toString(),
          );
        }
      }
      // The root element's inherited properties are moved to the page box
      // (the page context inherits from the root element), but properties that
      // UA stylesheets set with lang/dir attribute rules must also be set on
      // the root's own view element, which has those attributes copied from
      // the source root element. Otherwise the UA value matching the root's
      // view element would win over the author's value and be inherited by
      // all content. (Fix for issue #2167)
      if (!inheritedPropsMovedToPageBox || uaLangDirInheritedProps[propName]) {
        Base.setCSSProperty(target, propName, value.toString());
      }
    }
  }

  /**
   * Scales the lengths in units of the current font in a value to what the
   * browser resolves them to without the font-size adjustment: em against the
   * font size, and the other units against the font metrics of the font size
   * quantized by the browser. (Issue #1590)
   */
  private compensateFontSizeAdjustment(
    value: Css.Val,
    propName: string,
    fontSize: Css.Val | undefined,
    computedStyle: { [key: string]: Css.Val },
    target: Element,
  ): Css.Val {
    // A CSS-wide value that means inheritance, e.g. `font-size: inherit`,
    // takes the parent font size, as no declaration does.
    const emUnitSize = this.getEmUnitSize(
      propName,
      fontSize instanceof Css.Ident && /^(inherit|unset)$/i.test(fontSize.name)
        ? undefined
        : fontSize,
    );
    const adjustedEmUnitSize =
      emUnitSize != null ? emUnitSize - this.viewport.layoutUnitAdj : 0;
    if (!(adjustedEmUnitSize > 0)) {
      return value;
    }
    const emFactor = emUnitSize / adjustedEmUnitSize;
    const fontMetricFactor =
      this.getFontMetricsSize(emUnitSize) /
      this.getFontMetricsSize(adjustedEmUnitSize);
    let fontProperties: FontProperties | undefined;
    const compensator = new FontSizeAdjustmentCompensator((unit) => {
      if (unit === "em" || fontMetricFactor === emFactor) {
        return emFactor;
      }
      fontProperties ??= this.getCurrentFontProperties(
        propName,
        computedStyle,
        target,
      );
      return this.isFontMetricQuantized(unit, fontProperties)
        ? fontMetricFactor
        : emFactor;
    });
    const compensated = value.visit(compensator);
    // A negative length is invalid for a property that accepts only
    // non-negative lengths, e.g. `max-height: -1ex`, and the browser rejects
    // the declaration, while it accepts calc() of the length and clamps it to
    // zero. Such a value is kept as it is, so that it stays invalid. It is
    // checked under the names that Base.setCSSProperty() sets, e.g.
    // `-webkit-text-stroke-width` for `text-stroke-width`.
    if (compensator.scaledNegative) {
      const text = Css.withoutPageViewportUnits(value.toString());
      if (
        !Base.getPrefixedPropertyNames(propName)?.some((name) =>
          CSS.supports(name, text),
        )
      ) {
        return value;
      }
    }
    return compensated;
  }

  /**
   * Whether the font size that an element inherits from its view parent
   * carries the adjustment by layoutUnitAdj, found from the nearest font-size
   * declaration of the parent and its ancestors (the font size of the root
   * element is declared on the page box). (Issue #1590)
   */
  private isInheritedFontSizeAdjusted(): boolean {
    for (
      let node: Element | null = this.getParentViewNode();
      node;
      node = node.parentElement
    ) {
      const declaration = getInlineStyle(node)?.fontSize;
      if (declaration) {
        const adjusted = fontSizesWithoutParentAdjustment.has(node)
          ? false
          : fontSizeCarriesAdjustment(declaration);
        if (adjusted != null) {
          return adjusted;
        }
      }
    }
    return false;
  }

  /**
   * Whether a font-size function of the parent font size resolves to the same
   * length with and without the adjustment of the parent font size by
   * layoutUnitAdj, e.g. `clamp(16px, 1em, 24px)` with a 16px parent, so that
   * the font size of the element is not adjusted. (Issue #1590)
   */
  private resolvesWithoutParentAdjustment(fontSize: Css.Func): boolean {
    const parentFontSize = this.getParentComputedMetrics(
      this.getParentViewStyle(),
    ).fontSize;
    if (parentFontSize == null) {
      return false;
    }
    const size = CssCascade.resolveFontSizeValueToPx(
      this.context,
      fontSize,
      parentFontSize,
    );
    return (
      size != null &&
      size ===
        CssCascade.resolveFontSizeValueToPx(
          this.context,
          fontSize,
          parentFontSize - this.viewport.layoutUnitAdj,
        )
    );
  }

  /**
   * Returns the properties of the font that the units of the current font in
   * a value of a property resolve against: the parent's font for font-size,
   * and for the others the parent's font with the element's own font
   * properties.
   */
  private getCurrentFontProperties(
    propName: string,
    computedStyle: { [key: string]: Css.Val },
    target: Element,
  ): FontProperties {
    const properties = this.getFontProperties(this.getParentViewStyle());
    if (propName !== "font-size") {
      for (const name of fontMetricProperties) {
        const value = computedStyle[name];
        if (!value) {
          continue;
        }
        if (Css.canonicalWideKeyword(value) === Css.ident.initial) {
          // The probe has the initial value of a property it is not given,
          // while the other CSS-wide values keep the parent's value: these
          // properties are inherited.
          delete properties[name];
        } else if (!Css.isDefaultingValue(value)) {
          properties[name] = value.toString();
        }
      }
      const lang = Base.getLangAttribute(target);
      if (lang != null) {
        properties.lang = lang;
      }
    }
    return properties;
  }

  /**
   * Whether the browser resolves a unit of the font metrics (ex, cap, ch, ic)
   * of a font against the quantized font size (see getFontMetricsSize()). It
   * does when it takes the metric from the font, e.g. ic from the advance of
   * 水, but not when the font lacks it and the unit falls back to em, e.g. ic
   * for a Latin font, which then resolves against the font size itself. The
   * quantized size is the same for 100px and 100.002px at device pixel ratios
   * up to 4, while the font size itself differs. (Issue #1590)
   */
  private isFontMetricQuantized(
    unit: string,
    fontProperties: FontProperties,
  ): boolean {
    const size = this.context.measureFontUnit(unit, 100, fontProperties);
    const largerSize = this.context.measureFontUnit(
      unit,
      100.002,
      fontProperties,
    );
    return (
      size == null || largerSize == null || Math.abs(largerSize - size) < 5e-4
    );
  }

  /**
   * Returns the size of the font whose metrics (ex, cap, ch, ic) the browser
   * uses for a font size. Chromium floors the zoomed font size to a multiple
   * of 1/100 px (FontDescription::EffectiveFontSize()), and on Linux, ChromeOS
   * and Android, Skia's Fontations backend further truncates it to a multiple
   * of 1/64 px, like FreeType. So the metrics of a font size adjusted by
   * layoutUnitAdj can shrink by more than the adjustment. (Issue #1590)
   * @param fontSize font size in px
   */
  private getFontMetricsSize(fontSize: number): number {
    if (Base.browserType !== "chromium") {
      return fontSize;
    }
    const zoom = this.viewport.layoutUnitPerPixel / 64;
    // The small epsilons keep a size that is a multiple of the unit from
    // being floored to the next lower one by floating-point error.
    let size = Math.floor(fontSize * zoom * 100 + 1e-6) / 100;
    if (/\b(Linux|CrOS|Android)\b/.test(navigator.userAgent)) {
      size = Math.floor(size * 64 + 1e-6) / 64;
    }
    return size / zoom;
  }

  /**
   * Parse a CSS value with "px" unit plus layoutUnitAdj.
   * This is to get accurate font-size and line-height values from computed style
   * that have been set with minus layoutUnitAdj.
   * @param val CSS value string
   * @returns parsed and adjusted value in px, or null if cannot parse as "px" unit, e.g. "normal"
   */
  private parsePlusLayoutUnitAdj(
    val: string,
    inlineVal?: () => string | null | undefined,
  ): number | null {
    if (val.endsWith("px")) {
      const parsedVal = parseFloat(val);
      if (!isNaN(parsedVal)) {
        if (parsedVal === 0) {
          // A length is serialized as `calc(<length> - var(--viv-layoutUnitAdj))`,
          // and when the result is negative the browser clamps it to `0px`.
          // Adding the adjustment back would then turn an exact zero into
          // 1/64px (or 1/60px), so the length in front of the adjustment is
          // used instead. The declaration is only looked up for a zero value:
          // the lookup may walk the ancestors, and a value that is not zero
          // does not need it. (Review)
          const inline = inlineVal?.() ?? null;
          const base =
            inline != null ? parseLayoutUnitAdjustedValue(inline) : null;
          return base != null && base > 0 ? base : 0;
        }
        return (
          Math.round(
            (parsedVal + this.viewport.layoutUnitAdj) *
              this.viewport.layoutUnitPerPixel,
          ) / this.viewport.layoutUnitPerPixel
        );
      }
    }
    return null;
  }

  private getFootnoteAreaInheritedMetrics(): {
    fontSize: number | null;
    lineHeight: number | null;
  } {
    const pageContextElement = this.page.pageAreaElement?.parentElement
      ?.parentElement as HTMLElement | null;
    const pageContextStyle = pageContextElement
      ? this.viewport.window.getComputedStyle(pageContextElement)
      : null;
    const inlineStyle = getInlineStyle(pageContextElement);
    const fontSize =
      // A font size of 0 is valid and must not fall through to the defaults.
      (pageContextStyle
        ? this.parsePlusLayoutUnitAdj(
            pageContextStyle.fontSize,
            () => inlineStyle?.fontSize,
          )
        : null) ??
      this.context.rootFontSize ??
      this.context.initialFontSize;
    const lineHeight =
      // A line height of 0 is valid and must not fall through to the defaults.
      (pageContextStyle
        ? this.parsePlusLayoutUnitAdj(
            pageContextStyle.lineHeight,
            () => inlineStyle?.lineHeight,
          )
        : null) ??
      this.measureNormalLineHeight(
        pageContextStyle,
        fontSize,
        pageContextElement,
      ) ??
      this.context.rootLineHeight ??
      fontSize * Exprs.fallbackLineHeightRatio;
    return { fontSize, lineHeight };
  }

  private getComputedFontMetrics(
    element: Element,
    fallback: { fontSize: number | null; lineHeight: number | null },
  ): { fontSize: number | null; lineHeight: number | null } {
    const style = this.viewport.window.getComputedStyle(element);
    const inlineStyle = getInlineStyle(element);
    const fontSize =
      this.parsePlusLayoutUnitAdj(style.fontSize, () =>
        this.getLayoutUnitAdjustedBase(
          element,
          inlineStyle?.fontSize,
          "fontSize",
        ),
      ) ?? fallback.fontSize;
    const lineHeight =
      this.parsePlusLayoutUnitAdj(style.lineHeight, () =>
        this.getLayoutUnitAdjustedBase(
          element,
          inlineStyle?.lineHeight,
          "lineHeight",
        ),
      ) ??
      this.measureNormalLineHeight(style, fontSize, element) ??
      fallback.lineHeight ??
      (fontSize != null ? fontSize * Exprs.fallbackLineHeightRatio : null);
    return { fontSize, lineHeight };
  }

  /**
   * The inline declaration that the given inline value of an element has to be
   * read with to recover a length that the browser rounded down to zero: the
   * value of the element itself, or, because an element without a declaration
   * of its own inherits the length, the one of the nearest ancestor that has a
   * generated declaration. A declaration that is not an adjusted length, e.g.
   * `font-size: 0`, is kept, so that it cannot be replaced by an ancestor
   * value. (Review)
   */
  private getLayoutUnitAdjustedBase(
    element: Element | null,
    inlineVal: string | null | undefined,
    propName: "fontSize" | "lineHeight",
  ): string | null {
    if (inlineVal) {
      if (parseLayoutUnitAdjustedValue(inlineVal) != null) {
        return inlineVal;
      }
      // A CSS-wide value that means inheritance, e.g. the `font-size: inherit`
      // of this element itself, is not a length of its own: the search
      // continues with the ancestors, as it does for such a value of one of
      // them. (Review)
      if (!/^(inherit|unset)$/i.test(inlineVal.trim())) {
        return null;
      }
    }
    for (
      let node: Element | null = element?.parentElement ?? null;
      node;
      node = node.parentElement
    ) {
      // Only a chain in which the length rounded down to zero everywhere can
      // have inherited it from a generated declaration further up; an ancestor
      // whose computed value is not zero declares a length of its own.
      const computed = this.viewport.window.getComputedStyle(node)[propName];
      if (!(computed === "normal" || parseFloat(computed) === 0)) {
        return null;
      }
      const inline = getInlineStyle(node)?.[propName];
      if (inline) {
        if (parseLayoutUnitAdjustedValue(inline) != null) {
          return inline;
        }
        // A CSS-wide value that means inheritance, e.g. the `font-size:
        // inherit` of an element whose own length is the rounded down one, is
        // not a length of its own: the search continues with the ancestors,
        // whose chain rounded down to zero as well. (Review)
        if (!/^(inherit|unset)$/i.test(inline.trim())) {
          return null;
        }
      }
    }
    return null;
  }

  private getParentViewNode(): Element | null {
    return this.nodeContext?.parent?.viewNode?.nodeType === 1
      ? (this.nodeContext.parent.viewNode as Element)
      : null;
  }

  private getParentViewStyle(): CSSStyleDeclaration | null {
    const parentNode = this.getParentViewNode();
    return parentNode
      ? this.viewport.window.getComputedStyle(parentNode)
      : null;
  }

  private getParentComputedMetrics(parentStyle: CSSStyleDeclaration | null): {
    fontSize: number | null;
    lineHeight: number | null;
  } {
    const parentNode = this.getParentViewNode();
    const inlineStyle = getInlineStyle(parentNode);
    const fontSize =
      this.computedStyleParentFontSizeOverride ??
      (parentStyle
        ? this.parsePlusLayoutUnitAdj(parentStyle.fontSize, () =>
            this.getLayoutUnitAdjustedBase(
              parentNode,
              inlineStyle?.fontSize,
              "fontSize",
            ),
          )
        : this.context.rootFontSize);
    return {
      fontSize,
      lineHeight:
        this.computedStyleParentLineHeightOverride ??
        (parentStyle
          ? (this.parsePlusLayoutUnitAdj(parentStyle.lineHeight, () =>
              this.getLayoutUnitAdjustedBase(
                parentNode,
                inlineStyle?.lineHeight,
                "lineHeight",
              ),
            ) ??
            this.measureNormalLineHeight(parentStyle, fontSize, parentNode))
          : this.context.rootLineHeight),
    };
  }

  private getFontProperties(
    style: CSSStyleDeclaration | null,
    element: Element | null = this.getParentViewNode(),
  ): FontProperties {
    if (!style) return this.context.rootFontProperties;
    const properties: FontProperties = {};
    for (const name of fontMetricProperties)
      properties[name] = style.getPropertyValue(name);
    while (element) {
      const lang = Base.getLangAttribute(element);
      if (lang != null) {
        properties.lang = lang;
        break;
      }
      element = element.parentElement;
    }
    return properties;
  }

  private measureNormalLineHeight(
    style: CSSStyleDeclaration | null,
    fontSize: number | null,
    element: Element | null,
  ): number | null {
    return style?.lineHeight === "normal" && fontSize != null
      ? this.context.measureFontUnit(
          "lh",
          fontSize,
          this.getFontProperties(style, element),
        )
      : null;
  }

  /**
   * Get "lh" unit size in px
   * @return line-height in px, or null if cannot be determined
   */
  private getLineHeightUnitSize(
    propName: string,
    fontSize: Css.Val | undefined,
    lineHeight: Css.Val | undefined,
  ): number | null {
    const parentStyle = this.getParentViewStyle();
    const parentMetrics = this.getParentComputedMetrics(parentStyle);
    const parentLineHeight = parentMetrics.lineHeight;

    if (propName === "line-height" || propName === "font-size") {
      // For line-height property or for font-size property, return parent line-height.
      // Note: parentLineHeight may be null (cannot determine).
      return parentLineHeight;
    }

    // Font-size relative line-height value
    let lineHeightNum: number | null = null;

    if (lineHeight) {
      if (
        lineHeight instanceof Css.Num ||
        (lineHeight instanceof Css.Numeric &&
          (lineHeight.unit === "em" || lineHeight.unit === "%"))
      ) {
        // The line-height is relative to the font-size
        lineHeightNum =
          lineHeight instanceof Css.Numeric && lineHeight.unit === "%"
            ? lineHeight.num / 100
            : lineHeight.num;
      } else {
        if (
          lineHeight instanceof Css.Numeric &&
          /^(ex|cap|ch|ic)$/.test(lineHeight.unit)
        ) {
          // These units use this element's font, not the root font of the
          // page context. Let the browser resolve lh from its computed style.
          return null;
        }
        if (lineHeight instanceof Css.Numeric && lineHeight.unit !== "lh") {
          const unitSize = this.context.queryUnitSize(lineHeight.unit, false);
          if (unitSize != null) {
            // Return line-height in px
            return lineHeight.num * unitSize;
          }
        }
        return null; // cannot determine
      }
    }

    if (lineHeightNum == null) {
      // Try to get font-size relative line-height value from ancestor elements
      for (
        let viewNode: Node | null | undefined =
          this.nodeContext?.parent?.viewNode;
        viewNode && viewNode.nodeType === 1;
        viewNode = viewNode.parentNode
      ) {
        const ancestorLH = (viewNode as HTMLElement)?.style?.lineHeight;
        if (ancestorLH) {
          // Found line-height style in ancestor
          if (/^[0-9.]+$/.test(ancestorLH)) {
            // If it's a number without unit, it's relative to font-size
            lineHeightNum = parseFloat(ancestorLH);
          }
          break;
        }
      }
    }

    if (lineHeightNum != null) {
      // The line-height is relative to the font-size
      if (fontSize && !(fontSize instanceof Css.Numeric)) {
        return null; // cannot determine
      }
      const parentFontSize = parentMetrics.fontSize;
      if (fontSize instanceof Css.Numeric) {
        const fontSizePx = CssCascade.convertFontSizeToPx(
          fontSize,
          parentFontSize,
          this.context,
          undefined,
          usesFontMetrics(fontSize)
            ? this.getFontProperties(parentStyle)
            : undefined,
        );
        if (fontSizePx.unit !== "px" || !Number.isFinite(fontSizePx.num)) {
          return null; // cannot determine
        }
        return lineHeightNum * fontSizePx.num;
      } else {
        if (parentFontSize == null) {
          return null;
        }
        return lineHeightNum * parentFontSize;
      }
    }
    // Inherited normal is re-resolved in this element's font. Let the browser
    // resolve ordinary lh lengths after all of this element's font traits apply.
    if (parentStyle?.lineHeight === "normal") return null;
    // parentLineHeight may be null (cannot determine)
    return parentLineHeight;
  }

  /**
   * Get "em" unit size in px
   * @return font-size in px, or null if cannot be determined
   */
  private getEmUnitSize(
    propName: string,
    fontSize: Css.Val | undefined,
  ): number | null {
    const parentStyle = this.getParentViewStyle();
    const parentFontSize = this.getParentComputedMetrics(parentStyle).fontSize;

    if (propName === "font-size" || fontSize == null) {
      // For font-size property or when font-size is not specified, return parent font-size.
      // Note: parentFontSize may be null (cannot determine).
      return parentFontSize;
    }
    if (fontSize instanceof Css.Numeric) {
      // When font-size is specified, convert it to px
      const fontSizePx = CssCascade.convertFontSizeToPx(
        fontSize,
        parentFontSize,
        this.context,
        undefined,
        usesFontMetrics(fontSize)
          ? this.getFontProperties(parentStyle)
          : undefined,
      );
      if (fontSizePx.unit === "px" && Number.isFinite(fontSizePx.num)) {
        return fontSizePx.num;
      }
    }
    return null; // cannot determine
  }

  /** @override */
  applyPseudoelementStyle(
    nodeContext: Vtree.NodeContext,
    pseudoName: string,
    target: Element,
  ): void {
    if (nodeContext.after) {
      return;
    }
    const element = this.sourceNode as Element;
    const styler = nodeContext.shadowContext
      ? (nodeContext.shadowContext.styler as CssStyler.AbstractStyler)
      : this.styler;
    let elementStyle = styler.getStyle(element, false);
    const pseudoMap = CssCascade.getStyleMap(elementStyle, "_pseudos");
    if (!pseudoMap) {
      return;
    }
    elementStyle = pseudoMap[pseudoName];
    if (!elementStyle) {
      return;
    }
    const computedStyle: { [key: string]: Css.Val } = {};
    nodeContext.writingMode = this.computeStyle(
      nodeContext.writingMode,
      nodeContext.direction === "rtl",
      elementStyle,
      computedStyle,
    );
    nodeContext.vertical = nodeContext.writingMode !== "horizontal-tb";
    const content = computedStyle["content"];
    if (Vtree.nonTrivialContent(content)) {
      content.visit(
        new Vtree.ContentPropertyHandler(
          target,
          this.context,
          content,
          this.exprContentListener,
        ),
      );
      delete computedStyle["content"];
    }
    this.applyComputedStyles(target, computedStyle);
  }

  /** @override */
  peelOff(
    nodeContext: Vtree.ChildNodeContext,
    nodeOffset: number,
  ): Task.Result<Vtree.NodeContext> {
    const frame: Task.Frame<Vtree.NodeContext> = Task.newFrame("peelOff");
    const firstPseudo = nodeContext.firstPseudo;
    let offsetInNode = nodeContext.offsetInNode;
    const after = nodeContext.after;
    if (nodeOffset > 0) {
      offsetInNode += nodeOffset;
    } else if (!after && nodeContext.viewNode && offsetInNode == 0) {
      const parent = nodeContext.viewNode.parentNode;
      if (parent) {
        parent.removeChild(nodeContext.viewNode);
      }
    }
    const boxOffset = nodeContext.boxOffset + nodeOffset;

    // arr collects the chain strictly below its container, innermost first
    const arr: Vtree.NodeContext[] = [];
    let container: Vtree.NodeContext = nodeContext;
    for (
      let parent: Vtree.NodeContext | null = nodeContext.parent;
      parent && parent.firstPseudo === firstPseudo;
      parent = container.parent
    ) {
      arr.push(container);
      container = parent;
    }
    let shadowSibling = container.shadowSibling;
    let i = arr.length - 1;
    let rebuilt: Vtree.NodeContext | null = null;
    frame
      .loop(() => {
        while (i >= 0) {
          const pn = arr[i];
          const parentContext = rebuilt ?? container.parent;
          const child = new Vtree.NodeContext(
            pn.sourceNode,
            parentContext,
            boxOffset,
            (parentContext ?? container).formattingContext,
          );
          child.blockContainer =
            parentContext && Vtree.blockContainerForChildrenOf(parentContext);
          if (i == 0) {
            child.offsetInNode = offsetInNode;
            child.after = after;
          }
          child.shadowType = pn.shadowType;
          child.shadowContext = pn.shadowContext;
          child.nodeShadow = pn.nodeShadow;
          child.shadowSibling = pn.shadowSibling
            ? pn.shadowSibling
            : shadowSibling;
          shadowSibling = null;
          rebuilt = child;
          i--;
          const result = this.setCurrent(child, false);
          if (result.isPending()) {
            return result;
          }
        }
        return Task.newResult(false);
      })
      .then(() => {
        frame.finish(rebuilt ?? nodeContext.parent);
      });
    return frame.result();
  }

  createElement(ns: string | null, tag: string): Element {
    if (ns == Base.NS.XHTML) {
      return this.document.createElement(tag);
    }
    return this.document.createElementNS(ns, tag);
  }

  /** @override */
  applyFootnoteStyle(
    inheritedWritingMode: string,
    rtl: boolean,
    target: Element,
  ): Task.Result<string> {
    const computedStyle: { [key: string]: Css.Val } = {};
    const fetchers: TaskUtil.Fetcher<string>[] = [];

    let footnoteStyle = this.footnoteStyle;
    if (this.cascadedPageStyle) {
      const footnoteAreaMap = CssCascade.getStyleMap(
        this.cascadedPageStyle,
        CssPage.footnoteAreaKey,
      );
      if (footnoteAreaMap && footnoteAreaMap["area"]) {
        // Styles defined in @page @footnote override any top-level @footnote.
        footnoteStyle = footnoteAreaMap["area"];
      }
    }

    const pseudoMap = CssCascade.getStyleMap(footnoteStyle, "_pseudos");
    const writingMode = this.computeStyle(
      inheritedWritingMode,
      rtl,
      footnoteStyle,
      computedStyle,
    );
    // The used mode can be propagated from body while the page context's
    // computed mode stays horizontal. Match the browser's fragmentainer to
    // the resolved geometry rather than inheriting its unrelated DOM mode.
    computedStyle["writing-mode"] = Css.getName(writingMode);
    const inheritedMetrics = this.getFootnoteAreaInheritedMetrics();
    if (!computedStyle["font-size"] && inheritedMetrics.fontSize != null) {
      computedStyle["font-size"] = new Css.Numeric(
        inheritedMetrics.fontSize,
        "px",
      );
    }
    delete computedStyle["content"];
    const prevFontSizeOverride = this.computedStyleParentFontSizeOverride;
    const prevLineHeightOverride = this.computedStyleParentLineHeightOverride;
    // @footnote and @footnote::before resolve em/lh units from the page context,
    // not from the noteref that triggered semantic footnote generation.
    this.computedStyleParentFontSizeOverride = inheritedMetrics.fontSize;
    this.computedStyleParentLineHeightOverride = inheritedMetrics.lineHeight;
    try {
      this.applyComputedStyles(target, computedStyle);
      const targetMetrics = this.getComputedFontMetrics(
        target,
        inheritedMetrics,
      );
      this.computedStyleParentFontSizeOverride = targetMetrics.fontSize;
      this.computedStyleParentLineHeightOverride = targetMetrics.lineHeight;
      if (pseudoMap && pseudoMap["before"]) {
        const pseudoStyle = pseudoMap["before"];
        const hasSpecifiedDisplay = !!CssCascade.getProp(
          pseudoStyle,
          "display",
        );
        const childComputedStyle: { [key: string]: Css.Val } = {};
        const span = this.createElement(Base.NS.XHTML, "span");
        PseudoElement.setPseudoName(span, "before");
        target.appendChild(span);
        this.computeStyle(writingMode, rtl, pseudoStyle, childComputedStyle);
        if (!hasSpecifiedDisplay) {
          childComputedStyle["display"] = Css.ident.block;
        }

        const contentCascadeValue = CssCascade.getProp(pseudoStyle, "content");
        if (contentCascadeValue) {
          const contentElement =
            this.sourceNode?.nodeType === 1
              ? (this.sourceNode as Element)
              : this.xmldoc.root;
          childComputedStyle["content"] = contentCascadeValue
            .filterValue(
              new CssCascade.ContentPropVisitor(
                this.styler.cascade,
                contentElement,
                this.styler.cascade.counterResolver,
                this.styler.elementWindow.currentStyle,
                "before",
              ),
            )
            .evaluate(this.context, "content");
        }

        const content = childComputedStyle["content"];
        if (Vtree.nonTrivialContent(content)) {
          content.visit(
            new Vtree.ContentPropertyHandler(
              span,
              this.context,
              content,
              this.exprContentListener,
            ),
          );

          const images = span.querySelectorAll("img[src]");
          for (let i = 0; i < images.length; i++) {
            const image = images[i] as HTMLImageElement;
            const src = image.getAttribute("src");
            if (!src) {
              continue;
            }
            const fetcher = Net.loadElement(image, src);
            fetchers.push(fetcher);
            this.page.fetchers.push(fetcher);
          }

          const rootSrc = span.getAttribute("src");
          if (rootSrc) {
            let image = span.querySelector("img") as HTMLImageElement | null;
            if (!image) {
              image = this.document.createElement("img");
              image.setAttribute("src", rootSrc);
              span.appendChild(image);
            }
            const fetcher = Net.loadElement(image, rootSrc);
            fetchers.push(fetcher);
            this.page.fetchers.push(fetcher);
          }
        }
        delete childComputedStyle["content"];
        this.applyComputedStyles(span, childComputedStyle);
      }
    } finally {
      this.computedStyleParentFontSizeOverride = prevFontSizeOverride;
      this.computedStyleParentLineHeightOverride = prevLineHeightOverride;
    }
    if (fetchers.length) {
      return TaskUtil.waitForFetchers(fetchers).thenReturn(writingMode);
    }
    return Task.newResult(writingMode);
  }

  /** @override */
  processFragmentedBlockEdge(nodeContext: Vtree.NodeContext) {
    const nodeContext1 =
      !nodeContext.inline && nodeContext.after
        ? nodeContext.parent
        : nodeContext;
    // Fix for problem with math (Issue #1038)
    let isBeforeBlock = false;
    if (
      nodeContext.inline &&
      nodeContext.after &&
      !nodeContext.shadowContext &&
      nodeContext.sourceNode.nextSibling?.nodeType === 1
    ) {
      const nextElem = nodeContext.sourceNode.nextSibling as Element;
      const display = CssCascade.getProp(
        this.styler.getStyle(nextElem, false),
        "display",
      )?.value.toString();
      isBeforeBlock =
        (display && !Display.isInlineLevel(display)) ||
        (nextElem.getAttribute("data-math-typeset") === "true" &&
          /^\s*(\$\$|\\\[)/.test(nextElem.textContent));
    }

    let blockNestCount = 0;
    for (let nc = nodeContext1; nc; nc = nc.parent) {
      if (nc.viewNode?.nodeType !== 1) continue;

      const elem = nc.viewNode as HTMLElement;
      if (!elem.style) continue;

      if (nc.inline) {
        Break.setBoxBreakFlag(elem, "inline-end");
        if (Break.isCloneBoxDecorationBreak(elem)) {
          const extentBefore = nc.vertical
            ? elem.offsetWidth
            : elem.offsetHeight;
          Break.setBoxBreakFlag(elem, "clone");
          const extentAfter = nc.vertical
            ? elem.offsetWidth
            : elem.offsetHeight;
          if (extentAfter > extentBefore) {
            // Workaround for Chrome box-decoration-break:clone problem
            this.fixClonedBoxDecorationOverflow(elem);
          }
        }
      } else {
        const blockSizeP = nodeContext.vertical ? "width" : "height";
        if (Base.getCSSProperty(elem, blockSizeP)) {
          // When a box with a specified block size is fragmented,
          // the fragmented box should not have the block size.
          Base.setCSSProperty(elem, blockSizeP, "");
        }
        Break.setBoxBreakFlag(elem, "block-end");
        if (!blockNestCount++) {
          if (nc !== nodeContext1) {
            const { textAlign } = this.viewport.window.getComputedStyle(elem);
            if (textAlign === "justify" && !isBeforeBlock) {
              // Workaround for the problem that text-align-last:justify is
              // not only effective at the last line of the block but also
              // effective at lines just before child block-level or `<br>`
              // elements.
              const elem2 = this.createChildAnonymousBlockIfNeeded(elem);
              if (elem2) {
                if (elem2 !== elem) {
                  // child anonymous block created
                  Break.setBoxBreakFlags(elem2, [
                    "block-start",
                    "text-start",
                    "block-end",
                    "text-end",
                    "justify",
                  ]);
                } else {
                  Break.setBoxBreakFlag(elem, "text-end");
                  Break.setBoxBreakFlag(elem, "justify");
                }
              } else {
                // text-align-last:justify is not necessary (`<br>` at very last)
                // or not appropriate (e.g. the last `<br>` is in middle of nested inline)
                Break.setBoxBreakFlag(elem, "text-end");
              }
            } else {
              Break.setBoxBreakFlag(elem, "text-end");
            }
          }
        }
        if (Break.isCloneBoxDecorationBreak(elem)) {
          Break.setBoxBreakFlag(elem, "clone");
        }
        Break.setMarginDiscardFlag(elem, "block-end");
      }
    }
  }

  private fixClonedBoxDecorationOverflow(elem: HTMLElement): void {
    const computedStyle = this.viewport.window.getComputedStyle(elem);
    const paddingInlineEnd = parseFloat(computedStyle.paddingInlineEnd);
    const borderInlineEndWidth = parseFloat(computedStyle.borderInlineEndWidth);
    const adjust = -(paddingInlineEnd + borderInlineEndWidth);
    if (!isNaN(adjust)) {
      elem.style.marginInlineEnd = `${adjust}px`;
    }
  }

  private createChildAnonymousBlockIfNeeded(
    elem: HTMLElement,
  ): HTMLElement | null {
    const checkForcedLineBreakElem = (elem1: Element): boolean | null => {
      const { display, position, float } =
        this.viewport.window.getComputedStyle(elem1);
      if (elem1.localName === "ruby" || Base.mediaTags[elem1.localName]) {
        return false;
      }
      if (elem1.localName === "br") {
        return true;
      }
      if (
        (display === "inline" || display === "contents") &&
        elem1.hasChildNodes()
      ) {
        const lastChild = elem1.lastElementChild;
        if (
          lastChild &&
          (!lastChild.nextSibling ||
            (lastChild.nextSibling === elem1.lastChild &&
              Vtree.canIgnore(lastChild.nextSibling)))
        ) {
          const found = checkForcedLineBreakElem(lastChild);
          if (found || found === null) {
            return found;
          }
        }
        for (
          let child = lastChild?.previousElementSibling;
          child;
          child = child.previousElementSibling
        ) {
          const found = checkForcedLineBreakElem(child);
          if (found || found === null) {
            // forced line break is found at middle of nested inline
            return null;
          }
        }
        return false;
      }
      if (
        display === "none" ||
        position === "absolute" ||
        position === "fixed" ||
        (float && float !== "none") ||
        elem1.hasAttribute(LayoutHelper.SPECIAL_ATTR)
      ) {
        const prevElem = elem1.previousElementSibling;
        if (
          prevElem &&
          (prevElem.nextSibling === elem1 ||
            (prevElem.nextSibling === elem1.previousSibling &&
              Vtree.canIgnore(prevElem.nextSibling)))
        ) {
          return checkForcedLineBreakElem(prevElem);
        }
        return false;
      }
      if (!display || Display.isInlineLevel(display)) {
        return false;
      }
      return true;
    };

    let forcedBreakElem: Element | null = null;
    for (
      let child = elem.lastElementChild;
      child;
      child = child.previousElementSibling
    ) {
      const found = checkForcedLineBreakElem(child);
      if (found) {
        forcedBreakElem = child;
        break;
      }
      if (found === null) {
        // forced line break is found at middle of nested inline
        return null;
      }
    }

    if (!forcedBreakElem) {
      // forced line break is not found
      return elem;
    }

    if (
      forcedBreakElem === elem.lastElementChild &&
      (!forcedBreakElem.nextSibling ||
        (forcedBreakElem.nextSibling === elem.lastChild &&
          Vtree.canIgnore(forcedBreakElem.nextSibling)))
    ) {
      // forced line break is found at very last
      return null;
    }

    const anonymousBlock = elem.ownerDocument.createElement("span");
    anonymousBlock.className = "viv-anonymous-block";

    for (
      let node: Node | null = forcedBreakElem.nextSibling,
        nextNode: Node | null = null;
      node;
      node = nextNode
    ) {
      nextNode = node.nextSibling;
      anonymousBlock.appendChild(node);
    }
    elem.appendChild(anonymousBlock);
    return anonymousBlock;
  }

  /** @override */
  convertLengthToPx(
    numeric: Css.Numeric,
    viewNode: Node,
    clientLayout: Vtree.ClientLayout,
  ): number | Css.Numeric {
    const num = numeric.num;
    const unit = numeric.unit;
    if (Exprs.isFontRelativeLengthUnit(unit) || /^(ex|cap|ch|ic)$/.test(unit)) {
      let elem: Node | null = viewNode;
      while (elem && elem.nodeType !== 1) {
        elem = elem.parentNode;
      }
      Asserts.assert(elem);
      const style = clientLayout.getElementComputedStyle(elem as Element);
      const fontSize = parseFloat(style["font-size"]);
      const properties: FontProperties = {};
      for (const name of fontMetricProperties) properties[name] = style[name];
      return CssCascade.convertFontRelativeLengthToPx(
        numeric,
        fontSize,
        this.context,
        properties,
      ).num;
    } else {
      const unitSize = this.context.queryUnitSize(unit, false);
      if (unitSize) {
        return num * unitSize;
      } else {
        return numeric;
      }
    }
  }

  /**
   * Returns if two NodePositionStep are equivalent.
   */
  isSameNodePositionStep(
    step1: Vtree.NodePositionStep,
    step2: Vtree.NodePositionStep,
  ): boolean {
    if (step1.shadowContext) {
      if (!step2.shadowContext) {
        return false;
      }
      const elem1 =
        step1.node.nodeType === 1
          ? (step1.node as Element)
          : (step1.node.parentElement as Element);
      const elem2 =
        step2.node.nodeType === 1
          ? (step2.node as Element)
          : (step2.node.parentElement as Element);
      return (
        step1.shadowContext.owner === step2.shadowContext.owner &&
        PseudoElement.getPseudoName(elem1) ===
          PseudoElement.getPseudoName(elem2)
      );
    } else {
      return step1.node === step2.node;
    }
  }

  /** @override */
  isSameNodePosition(
    nodePosition1: Vtree.NodePosition,
    nodePosition2: Vtree.NodePosition,
  ): boolean {
    return (
      nodePosition1.offsetInNode === nodePosition2.offsetInNode &&
      nodePosition1.after == nodePosition2.after &&
      nodePosition1.steps.length === nodePosition2.steps.length &&
      nodePosition1.steps.every((step1, i) => {
        const step2 = nodePosition2.steps[i];
        return this.isSameNodePositionStep(step1, step2);
      })
    );
  }
}

export const propertiesNotPassedToDOM = {
  "float-min-wrap-block": true,
  "float-reference": true,
  "flow-into": true,
  "flow-linger": true,
  "flow-options": true,
  "flow-priority": true,
  "footnote-display": true,
  "footnote-policy": true,
  "margin-break": true,
  page: true,
};

/**
 * Inherited properties that UA stylesheets set with rules matching the lang
 * and dir attributes (e.g. `[lang|=ja] { text-emphasis-position: over right }`
 * and `[lang|=ja] { text-underline-position: right }` from the css-text-decor-4
 * default stylesheet, and the dir-attribute-to-direction mapping in HTML
 * rendering). These attributes are copied from the source root element to its
 * view element, so these properties must also be set on the root's view
 * element when the root's inherited properties are moved to the page box
 * (Issue #568), or the UA value would win over the author's value for all
 * content (Issue #2167). Other inherited properties must not be set on the
 * root's view element: parent-relative values (e.g. `font-size: larger`)
 * declared on both nested elements would be resolved twice and compound.
 */
export const uaLangDirInheritedProps: { [key: string]: boolean } = {
  "text-emphasis-position": true,
  "text-underline-position": true,
  direction: true,
};

export class DefaultClientLayout implements Vtree.ClientLayout {
  layoutBox: Element;
  window: Window;
  pixelRatio: number;
  scaleRatio: number;
  layoutUnitPerPixel: number;

  constructor(viewport: Viewport) {
    this.layoutBox = viewport.layoutBox;
    this.window = viewport.window;
    this.pixelRatio = viewport.pixelRatio;
    this.scaleRatio = viewport.scaleRatio;
    this.layoutUnitPerPixel = viewport.layoutUnitPerPixel;
  }

  private subtractOffsets(
    rect: Vtree.ClientRect,
    originRect: Vtree.ClientRect,
  ): Vtree.ClientRect {
    const viewportLeft = originRect.left;
    const viewportTop = originRect.top;
    return {
      left: rect.left - viewportLeft,
      top: rect.top - viewportTop,
      right: rect.right - viewportLeft,
      bottom: rect.bottom - viewportTop,
      width: rect.width,
      height: rect.height,
    } as Vtree.ClientRect;
  }

  /** @override */
  getRangeClientRects(range: Range): Vtree.ClientRect[] {
    const rects = range.getClientRects();
    const layoutBoxRect = this.layoutBox.getBoundingClientRect();
    return Array.from(rects).map((rect) =>
      this.subtractOffsets(rect, layoutBoxRect),
    );
  }

  /** @override */
  getElementClientRect(element: Element): Vtree.ClientRect {
    const htmlElement = element as HTMLElement;
    const rect = htmlElement.getBoundingClientRect() as Vtree.ClientRect;
    if (
      rect.left === 0 &&
      rect.top === 0 &&
      rect.right === 0 &&
      rect.bottom === 0
    ) {
      // getBoundingClientRect() returns 0,0,0,0 for WBR element (Chrome)
      // (Fix for issue #802)
      return rect;
    }
    const layoutBoxRect = this.layoutBox.getBoundingClientRect();
    return this.subtractOffsets(rect, layoutBoxRect);
  }

  /**
   * @param element element to measure
   * @return the client rects of the element's own boxes relative to the layout
   *     box, i.e. its border box fragments, without the rects of its contents
   * @override
   */
  getElementClientRects(element: Element): Vtree.ClientRect[] {
    const rects = element.getClientRects();
    const layoutBoxRect = this.layoutBox.getBoundingClientRect();
    return Array.from(rects).map((rect) =>
      this.subtractOffsets(rect, layoutBoxRect),
    );
  }

  /** @override */
  getElementComputedStyle(element: Element): CSSStyleDeclaration {
    return this.window.getComputedStyle(element, null);
  }

  /**
   * Adjust length value with rendering precision.
   * @param value Length value to adjust
   * @return Adjusted length value
   * @override
   */
  adjustLengthValue(value: number): number {
    return this.layoutUnitPerPixel
      ? Math.floor(value * this.layoutUnitPerPixel) / this.layoutUnitPerPixel
      : value;
  }
}

export class Viewport {
  document: Document;
  root: HTMLElement;
  private outerZoomBox: HTMLElement;
  contentContainer: HTMLElement;
  layoutBox: HTMLElement;
  width: number;
  height: number;
  scaleRatio: number = 1;
  layoutUnitPerPixel: number = 64;
  layoutUnitAdj: number = 0;

  constructor(
    public readonly window: Window,
    public readonly fontSize: number,
    public readonly pixelRatio: number,
    opt_root?: HTMLElement,
    opt_width?: number,
    opt_height?: number,
  ) {
    this.document = window.document;
    this.root = opt_root || this.document.body;

    if (this.root.hasAttribute("data-vivliostyle-toc-box")) {
      // TOC viewport does not need zoom box and layout box.
      this.outerZoomBox = this.contentContainer = this.layoutBox = this.root;
      this.width = opt_width || this.root.clientWidth;
      this.height = opt_height || this.root.clientHeight;
      return;
    }

    let outerZoomBox = this.root.firstElementChild as HTMLElement;
    if (!outerZoomBox) {
      outerZoomBox = this.document.createElement("div");
      outerZoomBox.setAttribute("data-vivliostyle-outer-zoom-box", "true");
      outerZoomBox.role = "presentation";
      this.root.appendChild(outerZoomBox);
    }
    let contentContainer = outerZoomBox.firstElementChild as HTMLElement;
    if (!contentContainer) {
      contentContainer = this.document.createElement("div");
      contentContainer.setAttribute(
        "data-vivliostyle-spread-container",
        "true",
      );
      contentContainer.role = "presentation";
      outerZoomBox.appendChild(contentContainer);
    }

    if (pixelRatio > 0 && CSS.supports("zoom", "8")) {
      // Emulate high pixel ratio using zoom and transform:scale().
      // Workaround for issue #419 (minimum border width too thick)
      // and #1076 (layout depends on device pixel ratio).
      //
      // CSS variables --viv-outputPixelRatio, --viv-devicePixelRatio,
      // and --viv-outputScale are used in zoom and transform property values
      // to emulate high pixel ratio output.
      // (see VivliostyleViewportCss in assets.ts)
      Base.setCSSProperty(this.root, "--viv-outputPixelRatio", `${pixelRatio}`);
      Base.setCSSProperty(
        this.root,
        "--viv-devicePixelRatio",
        `${window.devicePixelRatio}`,
      );

      this.scaleRatio = pixelRatio / window.devicePixelRatio;
    }

    // Determine layout unit per CSS pixel and adjustment value needed for
    // accurate layout of text and line boxes to avoid rounding issues
    // based on browser type. (Issue #1590)
    switch (Base.browserType) {
      case "chromium":
        this.layoutUnitPerPixel =
          64 * (this.pixelRatio || window.devicePixelRatio);
        this.layoutUnitAdj = 1 / this.layoutUnitPerPixel;
        break;
      case "firefox":
        this.layoutUnitPerPixel = 60;
        this.layoutUnitAdj = 1 / this.layoutUnitPerPixel;
        break;
      case "webkit":
      default:
        // WebKit uses 1/64 px layout unit, but it seems to floor
        // the position values instead of rounding them,
        // so text overflow does not occur without adjustment.
        this.layoutUnitPerPixel = 64;
        this.layoutUnitAdj = 0;
    }
    if (this.layoutUnitAdj) {
      Base.setCSSProperty(
        this.root,
        "--viv-layoutUnitAdj",
        `${this.layoutUnitAdj}px`,
      );
    }

    let layoutBox = outerZoomBox.nextElementSibling as HTMLElement;
    if (!layoutBox) {
      layoutBox = this.document.createElement("div");
      layoutBox.setAttribute("data-vivliostyle-layout-box", "true");
      layoutBox.role = "presentation";
      this.root.appendChild(layoutBox);
    }
    this.outerZoomBox = outerZoomBox;
    this.contentContainer = contentContainer;
    this.layoutBox = layoutBox;
    this.width =
      opt_width ||
      parseFloat(window.getComputedStyle(this.root).width) ||
      this.root.offsetWidth ||
      window.innerWidth;
    this.height =
      opt_height ||
      parseFloat(window.getComputedStyle(this.root).height) ||
      this.root.offsetHeight ||
      window.innerHeight;

    // Use the fallbackPageSize if window size is 0 or browser is in headless mode.
    const fallbackPageSize = {
      // compromise between A4 (210mm 297mm) and letter (8.5in 11in)
      width: 794, // 210mm (8.27in)
      height: 1056, // 279.4mm (11in)
    };
    const isHeadlessBrowser =
      (!window.outerWidth && !window.outerHeight) ||
      /Headless/.test(navigator.userAgent) ||
      (navigator.webdriver &&
        window.innerWidth === 800 &&
        window.innerHeight === 600);

    if (!this.width || (!opt_width && isHeadlessBrowser)) {
      this.width = fallbackPageSize.width;
    }
    if (!this.height || (!opt_height && isHeadlessBrowser)) {
      this.height = fallbackPageSize.height;
    }
  }

  /**
   * Reset zoom.
   */
  resetZoom() {
    Base.setCSSProperty(this.outerZoomBox, "width", "");
    Base.setCSSProperty(this.outerZoomBox, "height", "");
    Base.setCSSProperty(this.contentContainer, "width", "");
    Base.setCSSProperty(this.contentContainer, "height", "");
    Base.setCSSProperty(this.contentContainer, "transform", "");
  }

  /**
   * Zoom viewport.
   * @param width Overall width of contents before scaling (px)
   * @param height Overall height of contents before scaling (px)
   * @param scale Factor to which the viewport will be scaled.
   */
  zoom(width: number, height: number, scale: number) {
    Base.setCSSProperty(this.root, "--viv-outputScale", `${scale}`);
    Base.setCSSProperty(this.outerZoomBox, "width", `${width * scale}px`);
    Base.setCSSProperty(this.outerZoomBox, "height", `${height * scale}px`);
    Base.setCSSProperty(this.contentContainer, "width", `${width}px`);
    Base.setCSSProperty(this.contentContainer, "height", `${height}px`);
  }

  /**
   * Remove all pages inside the viewport.
   */
  clear() {
    const root = this.root;
    while (root.lastChild) {
      root.removeChild(root.lastChild);
    }
  }
}

/**
 * Recursively walk a CSS value tree and push image-load fetchers for any
 * URL values found. Used to preload images referenced by background-image,
 * border-image-source, filter, etc.
 */
export function addImageFetchersToPage(val: Css.Val, page: Vtree.Page): void {
  if (val instanceof Css.CommaList) {
    for (const v of val.values) {
      addImageFetchersToPage(v, page);
    }
  } else if (val instanceof Css.URL) {
    const url = Base.resolveWptResourceURL(val.url);
    page.fetchers.push(Net.loadElement(new Image(), url));
  } else if (val instanceof Css.Func) {
    for (const v of val.values) {
      addImageFetchersToPage(v, page);
    }
  } else if (val instanceof Css.SpaceList) {
    for (const v of val.values) {
      addImageFetchersToPage(v, page);
    }
  }
}

/**
 * The inline style declaration of an element, or null when it has none.
 */
function getInlineStyle(element: Element | null): CSSStyleDeclaration | null {
  return element ? ((element as HTMLElement).style ?? null) : null;
}

/**
 * The length in front of the layout unit adjustment of a serialized
 * `calc(<length>px - var(--viv-layoutUnitAdj))`, or null when the value does
 * not have that form. (Issue #2174 follow-up)
 */
function parseLayoutUnitAdjustedValue(val: string): number | null {
  const match =
    /^calc\(\s*(-?\d*\.?\d+(?:e[-+]?\d+)?)px\s*-\s*var\(--viv-layoutUnitAdj\)/i.exec(
      val.trim(),
    );
  if (!match) {
    return null;
  }
  const parsed = parseFloat(match[1]);
  return isNaN(parsed) ? null : parsed;
}
